import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CheckCircle,
    AlertCircle,
    Search,
    Edit2,
    Trash2,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    Plus,
    ChevronsUpDown,
    Building2,
    Mail,
    Phone,
    Globe,
    FileText,
    Download,
    Eye,
    Users,
    Layers,
    X,
    ShieldCheck,
    Calendar,
    Clock,
    DollarSign,
    Package,
    Upload,
    FileCheck,
    UserCheck,
    Loader2,
    RefreshCw,
    Star
} from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import './Dashboard.css';
import './DesignComponents.css';
import './SupplierList.css';
import './styles/pages-common.css';
import { usePageLoader } from '../components/common/PageLoader/PageLoader';
import {
    REGEX_STATUS,
    REGEX_GSTIN,
    validateEmail,
    validateTelephone,
    validateLettersOnly,
    validateAlphanumericSpecial,
    validateAddress,
    validateCity,
    validatePostalCode,
    validateGstNumber,
    validateUrl,
    validatePartNumber,
    sanitizePartNumber,
    isValidDate,
    validateDocumentName,
    validateEffectiveDate,
    validateExpiryDate,
    validateDocumentDates,
    mapDrfErrorsToFields,
    extractApiErrorMessage,
} from '../utils/validation';

const ROWS_PER_PAGE = 10;

const parseStatus = (val) => {
    if (val === false || val === 'false' || val === 'inactive' || val === 'deactive' || val === 0 || val === '0') {
        return false;
    }
    return true;
};

// ── Page-Level Form Validations (Calling Global Utility Functions) ───────────

/**
 * Validates Add / Edit Supplier Form against reference rules
 */
const validateSupplierForm = (form, suppliers = [], editingSupplier = null, states = []) => {
    const errors = {};

    // 1. Supplier Code * (Required, letters/nums/@&-_, unique)
    const code = String(form.code ?? '').trim();
    const codeErr = validateAlphanumericSpecial(code, { required: true, fieldName: 'Supplier code' });
    if (codeErr) {
        errors.code = code ? codeErr : 'This field is required.';
    } else {
        const isDuplicate = (suppliers || []).some(s =>
            s.code && s.code.trim().toLowerCase() === code.toLowerCase() &&
            (!editingSupplier || String(s.id) !== String(editingSupplier.id))
        );
        if (isDuplicate) {
            errors.code = 'This supplier code already exists. Please use a different code.';
        }
    }

    // 2. Supplier Name * (Required, letters/nums/@&-_)
    const name = String(form.name ?? '').trim();
    const nameErr = validateAlphanumericSpecial(name, { required: true, fieldName: 'Supplier name' });
    if (nameErr) {
        errors.name = name ? nameErr : 'This field is required.';
    }

    // 3. Registration Number (Optional)
    const regNoErr = validateAlphanumericSpecial(form.registrationNumber, { required: false });
    if (regNoErr) errors.registrationNumber = regNoErr;

    // 4. Tax/GST Number (Optional)
    const taxNumStr = String(form.taxNumber ?? '').trim();
    if (taxNumStr) {
        if (taxNumStr.length === 15 && (/^\d{2}[A-Za-z]/.test(taxNumStr) || !form.countryCode || form.countryCode.toUpperCase() === 'IN')) {
            const gstErr = validateGstNumber(taxNumStr, { required: false });
            if (gstErr) errors.taxNumber = gstErr;
        } else {
            const taxNoErr = validateAlphanumericSpecial(taxNumStr, { required: false });
            if (taxNoErr) errors.taxNumber = taxNoErr;
        }
    }

    // 5. Email Address * (Required, valid email format)
    const emailErr = validateEmail(form.email, { required: true, fieldName: 'Email address' });
    if (emailErr) errors.email = String(form.email ?? '').trim() ? emailErr : 'This field is required.';

    // 6. Telephone * (Required, digits/spaces/hyphens/parentheses, no '+', 7-15 digits)
    const phone = String(form.phone ?? '').trim();
    if (!phone) {
        errors.phone = 'This field is required.';
    } else {
        const phoneErr = validateTelephone(phone, { required: true, fieldName: 'Telephone' });
        if (phoneErr) errors.phone = phoneErr;
    }

    // 7. Address Line 1 * (Required)
    const addr1Err = validateAddress(form.address1, { required: true });
    if (addr1Err) errors.address1 = addr1Err;

    // 8. Address Line 2 (Optional)
    const addr2Err = validateAddress(form.address2, { required: false });
    if (addr2Err) errors.address2 = addr2Err;

    // 9. City * (Required, letters and spaces only)
    const cityErr = validateCity(form.city, { required: true });
    if (cityErr) errors.city = cityErr;

    // 10. Country * (Required)
    if (!form.country_id) {
        errors.country_id = 'Please select a country.';
    }

    // 11. State * (Required only if selected country has states in database)
    if (states && states.length > 0) {
        if (!form.state_id || !states.some(s => s.state_id === Number(form.state_id))) {
            errors.state_id = 'Please select a state belonging to the selected country.';
        }
    }

    // 12. Postal Code (Optional)
    const postalErr = validatePostalCode(form.postalCode, { required: false });
    if (postalErr) errors.postalCode = postalErr;

    // 13. Website / URL (Optional)
    const websiteErr = validateUrl(form.website, { required: false });
    if (websiteErr) errors.website = websiteErr;

    // 14. Warranty Terms * (Required)
    const warrantyTerms = String(form.warranty_terms ?? form.warrantyTerms ?? '').trim();
    if (!warrantyTerms) {
        errors.warranty_terms = 'Please select warranty terms.';
    }

    // 15. Supplier Status * (Required, letters/spaces// only)
    const supplierStatus = String(form.supplierStatus ?? '').trim();
    if (!supplierStatus) {
        errors.supplierStatus = 'This field is required.';
    } else if (!REGEX_STATUS.test(supplierStatus)) {
        errors.supplierStatus = 'Use letters, spaces, and / only.';
    }

    return errors;
};

/**
 * Validates Add / Edit Contact Form against reference rules
 */
const validateContactForm = (form, supplierId) => {
    const errors = {};

    // 1. Supplier (Required)
    if (!supplierId) {
        errors.supplier = 'Please select a supplier.';
    }

    // 2. Contact Name * (Required, letters & spaces only)
    const nameErr = validateLettersOnly(form.name, { required: true, fieldName: 'Contact name' });
    if (nameErr) errors.name = nameErr;

    // 3. Designation / Role (Optional)
    const roleErr = validateAlphanumericSpecial(form.designation, {
        required: false,
        fieldName: 'Designation / Role',
        customCharsDesc: 'letters, numbers, spaces, @, &, -, and _'
    });
    if (roleErr) errors.designation = roleErr;

    // 4. Email Address * (Required)
    const emailErr = validateEmail(form.email, { required: true, fieldName: 'Email address' });
    if (emailErr) errors.email = emailErr;

    // 5. Telephone * (Required, 7-15 digits, no '+')
    const phoneErr = validateTelephone(form.phone, { required: true, fieldName: 'Telephone' });
    if (phoneErr) errors.phone = phoneErr;

    // 6. Status * (Required)
    if (form.status === undefined || form.status === null || form.status === '') {
        errors.status = 'Please select a status.';
    }

    return errors;
};




const EMPTY_SUPPLIER_FORM = {
    code: '',
    name: '',
    registrationNumber: '',
    taxNumber: '',
    address1: '',
    address2: '',
    city: '',
    state_id: '',
    state: '',
    postalCode: '',
    country_id: '',
    country: '',
    countryCode: '',
    email: '',
    phone: '',
    website: '',
    payment_terms: '',
    paymentTerms: '',
    warranty_terms: '',
    warrantyTerms: '',
    supplierStatus: '',
    gstStatus: '',
    currency: '',
    paymentType: '',
    deliveryTerms: '',
    status: true,
};

const EMPTY_CONTACT_MODAL_FORM = {
    name: '',
    designation: '',
    email: '',
    phone: '',
    isPrimary: false,
    status: true,
};

const EMPTY_DOC_MODAL_FORM = {
    supplier_id: '',
    document_type_id: '',
    document_name: '',
    effective_date: '',
    expiry_date: '',
    existingFilename: '',
};

const EMPTY_COMP_FORM = {
    component_id: '',
    code: '',
    hsn_code: '',
    name: '',
    category: '',
    supplier_part_number: '',
    price: '',
    tax: '18.00',
    currency_code: 'INR',
    minimum_order_quantity: '10.00',
    lead_time_days: '15',
    payment_terms: '',
    warranty_terms: '',
    is_preferred: false,
    price_valid_until: '',
    status: true,
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '—';
    const clean = String(dateStr).trim();
    if (!clean || clean === '—' || clean === 'null' || clean === 'undefined') return '—';
    const datePart = clean.split('T')[0].split(' ')[0];
    const parts = datePart.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
        const [year, month, day] = parts;
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        if (!isNaN(monthNum) && !isNaN(dayNum) && monthNum >= 1 && monthNum <= 12) {
            const dd = String(dayNum).padStart(2, '0');
            const mmm = MONTH_NAMES[monthNum - 1];
            return `${dd}-${mmm}-${year}`;
        }
    }
    try {
        const d = new Date(clean);
        if (!isNaN(d.getTime())) {
            const dd = String(d.getDate()).padStart(2, '0');
            const mmm = MONTH_NAMES[d.getMonth()];
            const year = d.getFullYear();
            return `${dd}-${mmm}-${year}`;
        }
    } catch (_) { }
    return clean;
};

export const formatTwoDecimals = (val) => {
    if (val === null || val === undefined || val === '') return '0.00';
    const cleaned = String(val).replace(/,/g, '').replace(/[^0-9.-]/g, '');
    const num = parseFloat(cleaned);
    if (isNaN(num)) return '0.00';
    return num.toFixed(2);
};

const SupplierDatePicker = ({
    value,
    onChange,
    placeholder = 'DD-MMM-YYYY',
    required = false,
    min,
    max,
    disabled = false,
    className = '',
    error = false,
    name
}) => {
    const inputRef = useRef(null);
    const handleWrapClick = () => {
        if (disabled) return;
        if (inputRef.current) {
            try {
                if (typeof inputRef.current.showPicker === 'function') {
                    inputRef.current.showPicker();
                } else {
                    inputRef.current.focus();
                }
            } catch (_) {
                inputRef.current.focus();
            }
        }
    };
    return (
        <div
            className={`sup-datepick-wrap ${error ? 'error' : ''} ${disabled ? 'disabled' : ''} ${className}`}
            onClick={handleWrapClick}
        >
            <input
                ref={inputRef}
                type="date"
                name={name}
                className="sup-datepick-hidden"
                value={value || ''}
                onChange={onChange}
                required={required}
                min={min}
                max={max}
                disabled={disabled}
            />
            <div className="sup-datepick-display">
                <span className={value ? 'sup-datepick-val' : 'sup-datepick-ph'}>
                    {value ? formatDisplayDate(value) : placeholder}
                </span>
                <Calendar size={13} className="sup-datepick-icon" />
            </div>
        </div>
    );
};

export const SupplierList = ({ setView }) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });

    const [alert, setAlert] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { setPageLoading } = usePageLoader();

    useEffect(() => {
        setPageLoading(isLoading);
        return () => setPageLoading(false);
    }, [isLoading, setPageLoading]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Relational sub-state maps: supplierId -> items[]
    const [contactsMap, setContactsMap] = useState({});
    const [componentsMap, setComponentsMap] = useState({});
    const [documentsMap, setDocumentsMap] = useState({});
    const [loadingSubTabs, setLoadingSubTabs] = useState({});

    // Available Catalog Components for component dropdown mapping
    const [catalogComponents, setCatalogComponents] = useState([]);

    // Table & Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState('id');
    const [sortDir, setSortDir] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTopNav, setActiveTopNav] = useState('Supplier');

    // Expand state and active sub-tab per supplier
    const [expandedRows, setExpandedRows] = useState({});
    const [activeSubTabs, setActiveSubTabs] = useState({});

    // Supplier Add/Edit Modal
    const [showSupplierModal, setShowSupplierModal] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [supplierForm, setSupplierForm] = useState(EMPTY_SUPPLIER_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [isVerifyingGst, setIsVerifyingGst] = useState(false);
    const lastVerifiedGstRef = useRef('');

    // Dropdown lookup data: Countries, States, Payment Terms, Warranty Terms
    const [countries, setCountries] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [states, setStates] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const statesCache = useRef({});
    const [paymentTermsList, setPaymentTermsList] = useState([]);
    const [warrantyTermsList, setWarrantyTermsList] = useState([]);
    const [loadingWarrantyTerms, setLoadingWarrantyTerms] = useState(false);

    // Contact Add/Edit Modal (Popup)
    const [contactModalSupplierId, setContactModalSupplierId] = useState(null);
    const [editingContactId, setEditingContactId] = useState(null);
    const [contactFormData, setContactFormData] = useState(EMPTY_CONTACT_MODAL_FORM);
    const [contactFormErrors, setContactFormErrors] = useState({});

    // Document Modal State: Add / Upload Document (API: /api/supplier-documents/)
    const [docModalSupplierId, setDocModalSupplierId] = useState(null);
    const [editingDocId, setEditingDocId] = useState(null);
    const [selectedDocFile, setSelectedDocFile] = useState(null);
    const [docFileError, setDocFileError] = useState('');
    const [isDocSubmitting, setIsDocSubmitting] = useState(false);
    const [docFormData, setDocFormData] = useState(EMPTY_DOC_MODAL_FORM);
    const [docFormErrors, setDocFormErrors] = useState({});
    const [documentTypes, setDocumentTypes] = useState([]);
    const [loadingDocTypes, setLoadingDocTypes] = useState(false);
    const [previewingDoc, setPreviewingDoc] = useState(null);
    const [downloadingDocId, setDownloadingDocId] = useState(null);

    // Component Sourcing modal
    const [compModalSupplierId, setCompModalSupplierId] = useState(null);
    const [compFormData, setCompFormData] = useState(EMPTY_COMP_FORM);
    const [editingCompId, setEditingCompId] = useState(null);
    const [compFormErrors, setCompFormErrors] = useState({});

    // Delete Confirmation
    const [pendingDelete, setPendingDelete] = useState(null);

    // ── Fetch User Details ────────────────────────────────────────────────────
    const fetchUserDetails = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return null;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) throw new Error('Failed to fetch user');
            const data = await res.json();
            if (data.status && data.data?.user) {
                const u = data.data.user;
                if (u.profile_picture && !u.picture) u.picture = u.profile_picture;
                setUser(u);
                localStorage.setItem('user', JSON.stringify(u));
                return u;
            }
            throw new Error('Invalid');
        } catch {
            const stored = localStorage.getItem('user');
            if (stored) {
                const u = JSON.parse(stored);
                setUser(u);
                return u;
            }
            return null;
        }
    };

    // ── Fetch Suppliers (GET /api/suppliers/) ──────────────────────────────────
    const fetchSuppliers = useCallback(async () => {
        setIsLoading(true);
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                setIsLoading(false);
                return;
            }
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/suppliers/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
            });

            if (!res.ok) {
                if (res.status === 401) {
                    localStorage.removeItem('accessToken');
                    if (setView) setView('signin');
                    return;
                }
                throw new Error(`Failed to fetch suppliers (${res.status})`);
            }

            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) {
                list = data;
            } else if (data && data.status && Array.isArray(data.data)) {
                list = data.data;
            } else if (data && Array.isArray(data.data)) {
                list = data.data;
            } else if (data && Array.isArray(data.results)) {
                list = data.results;
            }

            const mapped = list.map(item => ({
                id: item.supplier_id ?? item.id,
                code: item.supplier_code ?? item.code ?? '',
                name: item.supplier_name ?? item.name ?? '',
                registrationNumber: item.registration_number ?? item.registrationNumber ?? '',
                taxNumber: item.tax_number ?? item.taxNumber ?? item.registration_number ?? '',
                address1: item.address_line_1 ?? item.address1 ?? '',
                address2: item.address_line_2 ?? item.address2 ?? '',
                city: item.city ?? '',
                state: item.state_name ?? item.state_region ?? item.state ?? '',
                state_id: item.state_id ?? (typeof item.state === 'number' ? item.state : null),
                postalCode: item.postal_code ?? item.postalCode ?? '',
                country: item.country_name ?? item.country_code ?? item.country ?? '',
                country_id: item.country_id ?? (typeof item.country === 'number' ? item.country : null),
                countryCode: item.country_code ?? '',
                email: item.email ?? '',
                phone: item.telephone ?? item.phone ?? '',
                website: item.website ?? '',
                paymentTerms: item.payment_terms_name ?? item.payment_terms ?? item.paymentTerms ?? '',
                payment_terms: item.payment_terms ?? item.payment_terms_id ?? null,
                warrantyTerms: item.warranty_terms_name ?? item.warranty_terms ?? item.warrantyTerms ?? '',
                warranty_terms: item.warranty_terms ?? item.warranty_terms_id ?? null,
                supplierStatus: item.supplier_status ?? item.supplierStatus ?? '',
                currency: item.currency_code ?? item.currency ?? '',
                paymentType: item.payment_type ?? item.paymentType ?? '',
                deliveryTerms: item.delivery_terms ?? item.deliveryTerms ?? '',
                status: parseStatus(item.status),
                created_utc: item.created_utc,
                created_by: item.created_by,
                last_update_date: item.last_update_date,
                last_updated_by: item.last_updated_by,
                documents: item.documents || [],
            }));

            setSuppliers(mapped);
        } catch (error) {
            console.error('[SupplierList] Error fetching suppliers:', error);
            setAlert({ type: 'error', message: error.message || 'Failed to load suppliers.' });
            setTimeout(() => setAlert(null), 4000);
            setSuppliers([]);
        } finally {
            setIsLoading(false);
        }
    }, [setView]);

    // ── Fetch Supplier Contacts (GET /api/supplier-contacts/?supplier_id=X) ───
    const fetchSupplierContacts = async (supplierId) => {
        if (!supplierId) return;
        setLoadingSubTabs(prev => ({
            ...prev,
            [supplierId]: { ...(prev[supplierId] || {}), contacts: true }
        }));
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-contacts/?supplier_id=${supplierId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) throw new Error(`Failed to load contacts (${res.status})`);
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;

            const mapped = list.map(c => ({
                id: c.supplier_contact_id ?? c.id,
                supplier_id: c.supplier_id,
                supplier_name: c.supplier_name,
                name: c.contact_name ?? c.name ?? '',
                designation: c.contact_role ?? c.designation ?? '',
                email: c.email ?? '',
                phone: c.telephone ?? c.phone ?? '',
                isPrimary: Boolean(c.is_primary),
                status: parseStatus(c.status),
                created_utc: c.created_utc,
                created_by: c.created_by,
                last_update_date: c.last_update_date,
                last_updated_by: c.last_updated_by,
            }));

            setContactsMap(prev => ({ ...prev, [supplierId]: mapped }));
        } catch (err) {
            console.error(`[SupplierList] Contacts fetch error for supplier ${supplierId}:`, err);
        } finally {
            setLoadingSubTabs(prev => ({
                ...prev,
                [supplierId]: { ...(prev[supplierId] || {}), contacts: false }
            }));
        }
    };

    // ── Fetch Supplier Components (GET /api/supplier-components/?supplier_id=X) ─
    const fetchSupplierComponents = async (supplierId) => {
        if (!supplierId) return;
        setLoadingSubTabs(prev => ({
            ...prev,
            [supplierId]: { ...(prev[supplierId] || {}), components: true }
        }));
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-components/?supplier_id=${supplierId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) throw new Error(`Failed to load components (${res.status})`);
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;

            const mapped = list.map(sc => ({
                id: sc.supplier_component_id ?? sc.id,
                supplier_id: sc.supplier_id,
                supplier_name: sc.supplier_name,
                component_id: sc.component_id,
                code: sc.component_code ?? sc.code ?? '',
                hsn_code: sc.hsn_code ?? sc.hsn ?? '',
                name: sc.component_name ?? sc.name ?? '',
                supplier_part_number: sc.supplier_part_number ?? '',
                price: formatTwoDecimals(sc.price ?? 0),
                tax: formatTwoDecimals(sc.tax ?? 0),
                total: typeof sc.total === 'number'
                    ? sc.total.toFixed(2)
                    : (sc.total ? formatTwoDecimals(sc.total) : (parseFloat(sc.price || 0) * (1 + (parseFloat(sc.tax || 0) / 100))).toFixed(2)),
                currency_code: sc.currency_code ?? 'INR',
                minimum_order_quantity: formatTwoDecimals(sc.minimum_order_quantity ?? 10),
                lead_time_days: sc.lead_time_days ?? 0,
                warranty_months: sc.warranty_months ?? 0,
                payment_terms: sc.payment_terms ?? sc.payment_terms_id ?? '',
                payment_terms_name: sc.payment_terms_name ?? (sc.payment_terms_detail ? sc.payment_terms_detail.payment_terms_name : ''),
                warranty_terms: sc.warranty_terms ?? sc.warranty_terms_id ?? '',
                warranty_terms_name: sc.warranty_terms_name ?? (sc.warranty_terms_detail ? sc.warranty_terms_detail.warranty_terms : ''),
                is_preferred: Boolean(sc.is_preferred),
                price_valid_until: sc.price_valid_until ?? '',
                status: parseStatus(sc.status),
                created_utc: sc.created_utc,
                created_by: sc.created_by,
                last_update_date: sc.last_update_date,
                last_updated_by: sc.last_updated_by,
            }));

            setComponentsMap(prev => ({ ...prev, [supplierId]: mapped }));
        } catch (err) {
            console.error(`[SupplierList] Components fetch error for supplier ${supplierId}:`, err);
        } finally {
            setLoadingSubTabs(prev => ({
                ...prev,
                [supplierId]: { ...(prev[supplierId] || {}), components: false }
            }));
        }
    };

    // ── Fetch Supplier Documents (GET /api/supplier-documents/?supplier_id=X) ─
    const fetchSupplierDocuments = async (supplierId) => {
        if (!supplierId) return;
        setLoadingSubTabs(prev => ({
            ...prev,
            [supplierId]: { ...(prev[supplierId] || {}), documents: true }
        }));
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-documents/?supplier_id=${supplierId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) throw new Error(`Failed to load documents (${res.status})`);
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;

            const mapped = list.map(d => ({
                id: d.supplier_document_id ?? d.id,
                supplier_document_id: d.supplier_document_id ?? d.id,
                supplier_id: d.supplier_id,
                supplier_name: d.supplier_name,
                document_type: d.document_type || '',
                document_type_id: d.document_type_id || d.type || '',
                type: d.document_type_id || d.type || '',
                document_name: d.document_name || d.name || '',
                name: d.document_name || d.name || '',
                original_filename: d.original_filename || d.originalFilename || '',
                originalFilename: d.original_filename || d.originalFilename || '',
                storage_key: d.storage_key || '',
                effective_date: d.effective_date || d.effectiveDate || '',
                effectiveDate: d.effective_date || d.effectiveDate || '',
                expiry_date: d.expiry_date || d.expiryDate || '',
                expiryDate: d.expiry_date || d.expiryDate || '',
                uploaded_utc: d.uploaded_utc || d.uploadedUtc || d.created_utc || '',
                uploadedUtc: d.uploaded_utc || d.uploadedUtc || d.created_utc || '',
                created_utc: d.created_utc,
                created_by: d.created_by,
                last_update_date: d.last_update_date,
                last_updated_by: d.last_updated_by,
                status: parseStatus(d.status),
            }));

            setDocumentsMap(prev => ({ ...prev, [supplierId]: mapped }));
        } catch (err) {
            console.error(`[SupplierList] Documents fetch error for supplier ${supplierId}:`, err);
        } finally {
            setLoadingSubTabs(prev => ({
                ...prev,
                [supplierId]: { ...(prev[supplierId] || {}), documents: false }
            }));
        }
    };

    // ── Fetch Catalog Components (GET /api/components/) ───────────────────────
    const fetchCatalogComponents = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/components/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) return;
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;

            setCatalogComponents(list.map(c => ({
                id: c.component_id ?? c.id,
                code: c.component_code ?? c.code ?? '',
                hsn_code: c.hsn_code ?? c.hsn ?? '',
                name: c.component_name ?? c.name ?? '',
                category: c.category_name ?? c.category ?? '',
                description: c.component_description ?? c.description ?? '',
                specification: c.specification ?? '',
            })));
        } catch (err) {
            console.error('[SupplierList] Error fetching catalog components:', err);
        }
    };

    // ── Fetch Document Types (GET /api/document-types/) ───────────────────────
    const fetchDocumentTypes = useCallback(async () => {
        setLoadingDocTypes(true);
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/document-types/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) {
                console.warn(`[SupplierList] Fetch document-types returned status ${res.status}`);
                return;
            }
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;

            if (list.length > 0) {
                const mapped = list.map(dt => ({
                    id: dt.document_type_id ?? dt.id,
                    name: dt.document_type_name ?? dt.name ?? dt.document_type ?? dt.type_name ?? dt.type ?? `Type #${dt.id}`,
                    code: dt.code ?? dt.document_type_code ?? '',
                    status: dt.status !== false,
                }));
                setDocumentTypes(mapped);
            }
        } catch (err) {
            console.error('[SupplierList] Error fetching document types:', err);
        } finally {
            setLoadingDocTypes(false);
        }
    }, []);

    // ── Fetch Countries (GET /api/countries) ──────────────────────────────────
    const fetchCountries = useCallback(async () => {
        setLoadingCountries(true);
        try {
            const accessToken = localStorage.getItem('accessToken');
            const headers = { 'Content-Type': 'application/json' };
            if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/countries`, {
                method: 'GET',
                headers,
            });
            if (!res.ok) {
                console.warn(`[SupplierList] Fetch countries returned status ${res.status}`);
                return;
            }
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;

            const mapped = (list || []).map(c => ({
                country_id: c.country_id ?? c.id,
                country_name: c.country_name ?? c.name,
                country_code: c.country_code ?? c.code,
                phonecode: c.phonecode,
                status: c.status !== false,
            }));
            setCountries(mapped);
        } catch (err) {
            console.error('[SupplierList] Error fetching countries:', err);
        } finally {
            setLoadingCountries(false);
        }
    }, []);

    // ── Fetch States by Country (GET /api/states?country_id=X) ────────────────
    const fetchStatesForCountry = useCallback(async (countryId) => {
        if (!countryId) {
            setStates([]);
            return [];
        }
        if (statesCache.current[countryId]) {
            setStates(statesCache.current[countryId]);
            return statesCache.current[countryId];
        }
        setLoadingStates(true);
        try {
            const accessToken = localStorage.getItem('accessToken');
            const headers = { 'Content-Type': 'application/json' };
            if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/states?country_id=${countryId}`, {
                method: 'GET',
                headers,
            });
            if (!res.ok) {
                console.warn(`[SupplierList] Fetch states returned status ${res.status}`);
                return [];
            }
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;

            const mapped = list.map(s => ({
                state_id: s.state_id ?? s.id,
                state_name: s.state_name ?? s.name,
                country_id: s.country_id,
                country_code: s.country_code,
                status: s.status !== false,
            }));
            statesCache.current[countryId] = mapped;
            setStates(mapped);
            return mapped;
        } catch (err) {
            console.error('[SupplierList] Error fetching states:', err);
            return [];
        } finally {
            setLoadingStates(false);
        }
    }, []);

    // ── Fetch Payment Terms Lookup (GET /api/payment-terms-lookup/) ───────────
    const fetchPaymentTerms = useCallback(async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            const headers = { 'Content-Type': 'application/json' };
            if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payment-terms-lookup/`, {
                method: 'GET',
                headers,
            });
            if (!res.ok) {
                console.warn(`[SupplierList] Fetch payment-terms returned status ${res.status}`);
                return;
            }
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;

            const mapped = (list || []).map(p => ({
                payment_terms_id: p.payment_terms_id ?? p.id,
                payment_terms_name: p.payment_terms_name ?? p.name ?? p.payment_terms ?? `Terms #${p.payment_terms_id ?? p.id}`,
                status: p.status !== false,
            }));
            setPaymentTermsList(mapped);
        } catch (err) {
            console.error('[SupplierList] Error fetching payment terms:', err);
        }
    }, []);

    // ── Fetch Warranty Terms Lookup (GET /api/warranty-terms-lookup/) ─────────
    const fetchWarrantyTerms = useCallback(async () => {
        setLoadingWarrantyTerms(true);
        try {
            const accessToken = localStorage.getItem('accessToken');
            const headers = { 'Content-Type': 'application/json' };
            if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/warranty-terms-lookup/`, {
                method: 'GET',
                headers,
            });
            if (!res.ok) {
                console.warn(`[SupplierList] Fetch warranty-terms returned status ${res.status}`);
                return;
            }
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;

            const mapped = (list || []).map(w => ({
                warranty_terms_id: w.warranty_terms_id ?? w.id,
                warranty_terms: w.warranty_terms ?? w.name ?? `Warranty #${w.warranty_terms_id ?? w.id}`,
                effective_date: w.effective_date,
                end_date: w.end_date,
            }));
            setWarrantyTermsList(mapped);
        } catch (err) {
            console.error('[SupplierList] Error fetching warranty terms:', err);
        } finally {
            setLoadingWarrantyTerms(false);
        }
    }, []);

    // ── Display Label Resolution Helpers ─────────────────────────────────────
    const getCountryLabel = useCallback((item) => {
        if (!item) return '—';
        if (item.country_id) {
            const found = countries.find(c => c.country_id === item.country_id);
            if (found) return found.country_name;
        }
        if (item.countryCode) {
            const found = countries.find(c => c.country_code.toLowerCase() === item.countryCode.toLowerCase());
            if (found) return found.country_name;
        }
        return item.country || item.countryCode || '—';
    }, [countries]);

    const getStateLabel = useCallback((item) => {
        if (!item) return '—';
        if (item.state_id && states.length > 0) {
            const found = states.find(s => s.state_id === item.state_id);
            if (found) return found.state_name;
        }
        return item.state || (item.state_id ? `State #${item.state_id}` : '—');
    }, [states]);

    const getPaymentTermsLabel = useCallback((item) => {
        if (!item) return '—';
        const ptVal = item.payment_terms ?? item.paymentTerms;
        if (ptVal !== null && ptVal !== undefined && ptVal !== '') {
            const found = paymentTermsList.find(p => p.payment_terms_id === Number(ptVal) || String(p.payment_terms_id) === String(ptVal));
            if (found) return found.payment_terms_name;
        }
        return (typeof item.paymentTerms === 'string' && item.paymentTerms) ? item.paymentTerms : (ptVal ? `Terms #${ptVal}` : '—');
    }, [paymentTermsList]);

    const getWarrantyTermsLabel = useCallback((item) => {
        if (!item) return '—';
        const wtVal = item.warranty_terms ?? item.warrantyTerms;
        if (wtVal !== null && wtVal !== undefined && wtVal !== '') {
            const found = warrantyTermsList.find(w => w.warranty_terms_id === Number(wtVal) || String(w.warranty_terms_id) === String(wtVal));
            if (found) return found.warranty_terms;
        }
        return (typeof item.warrantyTerms === 'string' && item.warrantyTerms) ? item.warrantyTerms : (wtVal ? `Warranty #${wtVal}` : '—');
    }, [warrantyTermsList]);

    // ── Initial Mount Effect ──────────────────────────────────────────────────
    useEffect(() => {
        let isMounted = true;
        fetchUserDetails().then(userData => {
            if (!userData && isMounted && setView) {
                setView('signin');
            }
        });
        fetchSuppliers();
        fetchCatalogComponents();
        fetchDocumentTypes();
        fetchCountries();
        fetchPaymentTerms();
        fetchWarrantyTerms();

        return () => {
            isMounted = false;
        };
    }, [fetchSuppliers, fetchDocumentTypes, fetchCountries, fetchPaymentTerms, fetchWarrantyTerms, setView]);

    // ── Search & Filter Logic ─────────────────────────────────────────────────
    const filtered = (suppliers || []).filter(item => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const countryLabel = getCountryLabel(item).toLowerCase();
        const stateLabel = getStateLabel(item).toLowerCase();
        const ptLabel = getPaymentTermsLabel(item).toLowerCase();
        const wtLabel = getWarrantyTermsLabel(item).toLowerCase();
        return (
            (item.code || '').toLowerCase().includes(q) ||
            (item.name || '').toLowerCase().includes(q) ||
            (item.email || '').toLowerCase().includes(q) ||
            (item.phone || '').toLowerCase().includes(q) ||
            (item.city || '').toLowerCase().includes(q) ||
            (item.country || '').toLowerCase().includes(q) ||
            countryLabel.includes(q) ||
            stateLabel.includes(q) ||
            ptLabel.includes(q) ||
            wtLabel.includes(q) ||
            (item.supplierStatus || '').toLowerCase().includes(q)
        );
    });

    const sorted = [...filtered].sort((a, b) => {
        let av = a[sortField];
        let bv = b[sortField];
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const totalPages = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
    const paginated = sorted.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    // ── Expand Row Handler ────────────────────────────────────────────────────
    const toggleRowExpand = (id) => {
        setExpandedRows(prev => {
            const nextVal = !prev[id];
            if (nextVal) {
                if (!activeSubTabs[id]) {
                    setActiveSubTabs(t => ({ ...t, [id]: 'details' }));
                }
                // Preload contacts, components, and documents if not already loaded
                if (!contactsMap[id]) fetchSupplierContacts(id);
                if (!componentsMap[id]) fetchSupplierComponents(id);
                if (!documentsMap[id]) fetchSupplierDocuments(id);
            }
            return { ...prev, [id]: nextVal };
        });
    };

    const setSubTab = (supplierId, tabName) => {
        setActiveSubTabs(prev => ({ ...prev, [supplierId]: tabName }));
        if (tabName === 'contacts' && !contactsMap[supplierId]) {
            fetchSupplierContacts(supplierId);
        } else if (tabName === 'components' && !componentsMap[supplierId]) {
            fetchSupplierComponents(supplierId);
        } else if (tabName === 'documents' && !documentsMap[supplierId]) {
            fetchSupplierDocuments(supplierId);
        }
    };

    // ── Toggle Status on Main Row (PATCH /api/suppliers/{id}/status/) ─────────
    const handleToggleStatus = async (supplierId, currentStatus, e) => {
        e && e.stopPropagation();
        const nextStatus = !currentStatus;
        const accessToken = localStorage.getItem('accessToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/suppliers/${supplierId}/status/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: nextStatus })
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Status update failed (${res.status})`));
            }

            // Update supplier status only — keep the supplier row visible and preserve all child items as active
            setSuppliers(prev =>
                prev.map(c => (c.id === supplierId ? { ...c, status: nextStatus } : c))
            );

            setAlert({
                type: 'success',
                message: `Supplier status set to ${nextStatus ? 'Active' : 'Inactive'}.`,
            });
            setTimeout(() => setAlert(null), 3000);
        } catch (err) {
            console.error('[SupplierList] Status change error:', err);
            setAlert({ type: 'error', message: err.message || 'Failed to update status.' });
            setTimeout(() => setAlert(null), 4000);
        }
    };

    // ── Open Add Supplier Modal ───────────────────────────────────────────────
    const handleOpenAddSupplier = () => {
        setEditingSupplier(null);
        lastVerifiedGstRef.current = '';
        const maxNum = (suppliers || []).reduce((max, s) => {
            const match = (s.code || '').match(/SUP-(\d+)/i);
            return match ? Math.max(max, parseInt(match[1], 10)) : max;
        }, 0);
        const nextCode = `SUP-${String(Math.max(maxNum + 1, (suppliers || []).length + 1)).padStart(4, '0')}`;

        setSupplierForm({
            ...EMPTY_SUPPLIER_FORM,
            code: nextCode,
        });
        setStates([]);
        setFormErrors({});
        setShowSupplierModal(true);
        if (paymentTermsList.length === 0) fetchPaymentTerms();
        if (warrantyTermsList.length === 0) fetchWarrantyTerms();
        if (countries.length === 0) fetchCountries();
    };

    // ── Open Edit Supplier Modal ──────────────────────────────────────────────
    const handleOpenEditSupplier = (sup) => {
        setEditingSupplier(sup);
        lastVerifiedGstRef.current = sup.taxNumber ? String(sup.taxNumber).trim().toUpperCase() : '';

        // Resolve country_id
        let cId = sup.country_id ?? '';
        if (!cId && countries.length > 0) {
            const match = countries.find(c =>
                c.country_id === sup.country_id ||
                c.country_code.toLowerCase() === (sup.countryCode || sup.country || '').toLowerCase() ||
                c.country_name.toLowerCase() === (sup.country || '').toLowerCase()
            );
            if (match) cId = match.country_id;
        }

        // Resolve payment_terms
        let ptId = sup.payment_terms ?? '';
        if (!ptId && sup.paymentTerms && paymentTermsList.length > 0) {
            const match = paymentTermsList.find(p =>
                p.payment_terms_id === sup.payment_terms ||
                p.payment_terms_id === Number(sup.paymentTerms) ||
                p.payment_terms_name.toLowerCase() === String(sup.paymentTerms).toLowerCase()
            );
            if (match) ptId = match.payment_terms_id;
        }

        // Resolve warranty_terms
        let wtId = sup.warranty_terms ?? '';
        if (!wtId && sup.warrantyTerms && warrantyTermsList.length > 0) {
            const match = warrantyTermsList.find(w =>
                w.warranty_terms_id === sup.warranty_terms ||
                w.warranty_terms_id === Number(sup.warrantyTerms) ||
                w.warranty_terms.toLowerCase() === String(sup.warrantyTerms).toLowerCase()
            );
            if (match) wtId = match.warranty_terms_id;
        }

        setSupplierForm({
            code: sup.code || '',
            name: sup.name || '',
            registrationNumber: sup.registrationNumber || sup.taxNumber || '',
            taxNumber: sup.taxNumber || '',
            address1: sup.address1 || '',
            address2: sup.address2 || '',
            city: sup.city || '',
            state_id: sup.state_id ?? '',
            state: sup.state || '',
            postalCode: sup.postalCode || '',
            country_id: cId,
            country: sup.country || '',
            countryCode: sup.countryCode || '',
            email: sup.email || '',
            phone: sup.phone || '',
            website: sup.website || '',
            payment_terms: ptId,
            paymentTerms: sup.paymentTerms || '',
            warranty_terms: wtId,
            warrantyTerms: sup.warrantyTerms || '',
            supplierStatus: sup.supplierStatus || '',
            gstStatus: sup.gstStatus || sup.gst_status || '',
            currency: sup.currency || '',
            paymentType: sup.paymentType || '',
            deliveryTerms: sup.deliveryTerms || '',
            status: sup.status ?? true,
        });
        setFormErrors({});
        setShowSupplierModal(true);

        if (cId) {
            fetchStatesForCountry(cId).then(stList => {
                if (stList && stList.length > 0) {
                    let matchedStId = sup.state_id;
                    if (!matchedStId && sup.state) {
                        const match = stList.find(s =>
                            s.state_name.toLowerCase() === String(sup.state).toLowerCase() ||
                            String(s.state_id) === String(sup.state)
                        );
                        if (match) matchedStId = match.state_id;
                    }
                    if (matchedStId) {
                        const stateObj = stList.find(s => s.state_id === matchedStId);
                        setSupplierForm(prev => ({
                            ...prev,
                            state_id: matchedStId,
                            state: stateObj ? stateObj.state_name : prev.state
                        }));
                    }
                }
            });
        } else {
            setStates([]);
        }
    };

    // ── Helper to clear auto-filled GST fields on wrong GST number ────────────
    const clearAutoFilledGstFields = () => {
        setSupplierForm(prev => ({
            ...prev,
            name: '',
            address1: '',
            city: '',
            state: '',
            state_id: '',
            postalCode: '',
            gstStatus: '',
        }));
    };

    // ── Verify GST Details (GET /api/gst/verify/{gstNumber}/) ─────────────────
    const verifyGstDetails = async (gstNumber) => {
        const cleanGst = String(gstNumber || '').trim().toUpperCase();
        if (!cleanGst) return;

        if (cleanGst.length !== 15) {
            clearAutoFilledGstFields();
            setFormErrors(prev => ({ ...prev, taxNumber: 'GST number must be 15 characters (e.g. 06AAFCV5179D2Z7).' }));
            return;
        }

        if (!REGEX_GSTIN.test(cleanGst)) {
            const formatErr = 'Invalid GST format (e.g. 06AAFCV5179D2Z7).';
            clearAutoFilledGstFields();
            setFormErrors(prev => ({ ...prev, taxNumber: formatErr }));
            setAlert({ type: 'error', message: formatErr });
            setTimeout(() => setAlert(null), 4000);
            return;
        }

        lastVerifiedGstRef.current = cleanGst;
        setIsVerifyingGst(true);

        try {
            const accessToken = localStorage.getItem('accessToken');
            const headers = { 'Content-Type': 'application/json' };
            if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/gst/verify/${encodeURIComponent(cleanGst)}/`, {
                method: 'GET',
                headers,
            });

            if (!res.ok) {
                const errJson = await res.json().catch(() => ({}));
                const errMsg = extractApiErrorMessage(errJson, errJson.message || `Invalid GST number or verification failed (${res.status})`);
                console.warn('[SupplierList] GST verify API returned non-OK status:', res.status, errMsg);
                clearAutoFilledGstFields();
                setFormErrors(prev => ({ ...prev, taxNumber: errMsg }));
                setAlert({
                    type: 'error',
                    message: errMsg,
                });
                setTimeout(() => setAlert(null), 4500);
                return;
            }

            const resData = await res.json();
            if (resData && resData.success === false) {
                const errMsg = resData.message || resData.error || 'Invalid GST number. Verification failed.';
                clearAutoFilledGstFields();
                setFormErrors(prev => ({ ...prev, taxNumber: errMsg }));
                setAlert({
                    type: 'error',
                    message: errMsg,
                });
                setTimeout(() => setAlert(null), 4500);
                return;
            }

            const gstData = (resData && typeof resData === 'object' && resData.data) ? resData.data : resData;

            if (!gstData || typeof gstData !== 'object' || (!gstData.legal_name && !gstData.trade_name && !gstData.gstin)) {
                const errMsg = (resData && (resData.message || resData.error)) || 'No business details found for this GST number.';
                clearAutoFilledGstFields();
                setFormErrors(prev => ({ ...prev, taxNumber: errMsg }));
                setAlert({
                    type: 'error',
                    message: errMsg,
                });
                setTimeout(() => setAlert(null), 4500);
                return;
            }

            if (gstData && typeof gstData === 'object') {
                const legalName = gstData.legal_name || gstData.trade_name || '';
                const gstState = gstData.state || '';
                const gstAddress = gstData.address ? String(gstData.address).replace(/\r?\n|\r/g, ', ').replace(/\s+/g, ' ').trim() : '';
                const gstPincode = gstData.pincode || '';

                // Resolve District / City across various direct GST keys & address fallbacks
                let gstDistrict = '';
                const directCandidates = [
                    gstData.district,
                    gstData.city,
                    gstData.addr_city,
                    gstData.address_district,
                    gstData.dst,
                    gstData.pradr?.addr?.dst,
                    gstData.pradr?.addr?.loc,
                    gstData.pradr?.addr?.city,
                    gstData.principal_place_of_business?.district,
                    gstData.principal_place_of_business?.city,
                    gstData.principal_place_of_business?.address?.district,
                    gstData.principal_place_of_business?.address?.dst,
                    gstData.principal_place_of_business?.address?.city,
                    gstData.data?.district,
                    gstData.data?.city,
                    gstData.adadr?.[0]?.addr?.dst,
                    gstData.adadr?.[0]?.addr?.loc,
                ];
                for (const cand of directCandidates) {
                    if (cand && typeof cand === 'string' && cand.trim().length > 0) {
                        gstDistrict = cand.trim();
                        break;
                    }
                }

                // Fallback: parse district from address string if direct field was omitted
                if (!gstDistrict) {
                    const rawAddress = String(gstData.address || gstData.pradr?.addr?.adr || '');
                    if (rawAddress) {
                        const cleaned = rawAddress.replace(/[—–-]?\s*\b\d{6}\b\s*.*$/g, '').trim();
                        const parts = cleaned.split(/[,;\n\r]+/).map(p => p.trim()).filter(Boolean);
                        if (parts.length > 0) {
                            const stateTarget = String(gstState || '').trim().toLowerCase();
                            for (let i = parts.length - 1; i >= 0; i--) {
                                const part = parts[i].trim();
                                const partLower = part.toLowerCase();
                                if (stateTarget && (partLower === stateTarget || partLower.includes(stateTarget))) {
                                    continue;
                                }
                                if (/^\d+$/.test(part) || part.length <= 2) {
                                    continue;
                                }
                                gstDistrict = part;
                                break;
                            }
                        }
                    }
                }

                // 1. Resolve Country (Default to India if none selected)
                let targetCountryId = supplierForm.country_id;
                let targetCountry = supplierForm.country;
                let targetCountryCode = supplierForm.countryCode;

                let currentCountries = countries;
                if (!targetCountryId) {
                    if (currentCountries.length === 0) {
                        try {
                            const cRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/countries`, { method: 'GET', headers });
                            if (cRes.ok) {
                                const cData = await cRes.json();
                                let cList = [];
                                if (Array.isArray(cData)) cList = cData;
                                else if (cData && cData.status && Array.isArray(cData.data)) cList = cData.data;
                                else if (cData && Array.isArray(cData.data)) cList = cData.data;
                                currentCountries = (cList || []).map(c => ({
                                    country_id: c.country_id ?? c.id,
                                    country_name: c.country_name ?? c.name,
                                    country_code: c.country_code ?? c.code,
                                    phonecode: c.phonecode,
                                    status: c.status !== false,
                                }));
                                setCountries(currentCountries);
                            }
                        } catch (_) { }
                    }
                    const india = currentCountries.find(c =>
                        (c.country_code && c.country_code.toUpperCase() === 'IN') ||
                        (c.country_name && c.country_name.toLowerCase() === 'india')
                    );
                    if (india) {
                        targetCountryId = india.country_id;
                        targetCountry = india.country_name;
                        targetCountryCode = india.country_code;
                    }
                }

                // 2. Resolve States for Country and Match State
                let matchedStateId = '';
                let matchedStateName = gstState;

                if (targetCountryId) {
                    let currentStatesList = states;
                    if (statesCache.current[targetCountryId]) {
                        currentStatesList = statesCache.current[targetCountryId];
                        setStates(currentStatesList);
                    } else {
                        currentStatesList = await fetchStatesForCountry(targetCountryId);
                    }

                    if (Array.isArray(currentStatesList) && currentStatesList.length > 0 && gstState) {
                        const cleanTarget = String(gstState).trim().toLowerCase();
                        const stMatch = currentStatesList.find(s => {
                            const sName = String(s.state_name || '').trim().toLowerCase();
                            return sName === cleanTarget || sName.includes(cleanTarget) || cleanTarget.includes(sName);
                        });
                        if (stMatch) {
                            matchedStateId = stMatch.state_id;
                            matchedStateName = stMatch.state_name;
                        }
                    }
                }

                // Resolve GST Status (e.g. "Active" if returned by API, otherwise empty)
                const gstStatusVal = (gstData && (gstData.status || gstData.taxpayer_status || gstData.gstin_status)) ? String(gstData.status || gstData.taxpayer_status || gstData.gstin_status).trim() : '';

                // 3. Update supplier form with auto-filled fields
                setSupplierForm(prev => ({
                    ...prev,
                    taxNumber: cleanGst,
                    ...(legalName ? { name: legalName } : {}),
                    ...(gstAddress ? { address1: gstAddress } : {}),
                    ...(gstPincode ? { postalCode: gstPincode } : {}),
                    ...(gstDistrict ? { city: gstDistrict } : {}),
                    gstStatus: gstStatusVal,
                    ...(gstStatusVal ? {
                        status: String(gstStatusVal).toLowerCase() === 'active',
                    } : {}),
                    ...(gstState ? { state: matchedStateName || gstState } : {}),
                    ...(matchedStateId ? { state_id: matchedStateId } : {}),
                    ...(targetCountryId ? {
                        country_id: targetCountryId,
                        country: targetCountry,
                        countryCode: targetCountryCode
                    } : {}),
                }));

                // 4. Clear any existing form errors on these auto-filled fields
                setFormErrors(prev => {
                    const next = { ...prev };
                    if (legalName) delete next.name;
                    if (gstAddress) delete next.address1;
                    if (gstPincode) delete next.postalCode;
                    if (matchedStateId || gstState) delete next.state_id;
                    if (targetCountryId) delete next.country_id;
                    if (gstDistrict) delete next.city;
                    delete next.taxNumber;
                    delete next.submit;
                    return next;
                });

                setAlert({
                    type: 'success',
                    message: `GST details verified for "${legalName || cleanGst}". Form fields auto-filled.`
                });
                setTimeout(() => setAlert(null), 4000);
            }
        } catch (err) {
            console.error('[SupplierList] Error verifying GST:', err);
            const errMsg = err.message || 'Failed to verify GST number.';
            clearAutoFilledGstFields();
            setFormErrors(prev => ({ ...prev, taxNumber: errMsg }));
            setAlert({
                type: 'error',
                message: errMsg
            });
            setTimeout(() => setAlert(null), 4000);
        } finally {
            setIsVerifyingGst(false);
        }
    };

    // ── Supplier Form Change Handler ──────────────────────────────────────────
    const handleSupplierFormChange = (e) => {
        const { name, value } = e.target;
        if (name === 'country_id') {
            const countryIdNum = value ? Number(value) : '';
            const matchedCountry = countries.find(c => c.country_id === countryIdNum);
            setSupplierForm(prev => ({
                ...prev,
                country_id: countryIdNum,
                countryCode: matchedCountry ? matchedCountry.country_code : '',
                country: matchedCountry ? matchedCountry.country_name : '',
                state_id: '',
                state: '',
            }));
            if (countryIdNum) {
                fetchStatesForCountry(countryIdNum);
            } else {
                setStates([]);
            }
        } else if (name === 'state_id') {
            const stateIdNum = value ? Number(value) : '';
            const matchedState = states.find(s => s.state_id === stateIdNum);
            setSupplierForm(prev => ({
                ...prev,
                state_id: stateIdNum,
                state: matchedState ? matchedState.state_name : '',
            }));
        } else if (name === 'payment_terms') {
            const ptNum = value ? Number(value) : '';
            const matchedPt = paymentTermsList.find(p => p.payment_terms_id === ptNum);
            setSupplierForm(prev => ({
                ...prev,
                payment_terms: ptNum,
                paymentTerms: matchedPt ? matchedPt.payment_terms_name : '',
            }));
        } else if (name === 'warranty_terms') {
            const wtNum = value ? Number(value) : '';
            const matchedWt = warrantyTermsList.find(w => w.warranty_terms_id === wtNum);
            setSupplierForm(prev => ({
                ...prev,
                warranty_terms: wtNum,
                warrantyTerms: matchedWt ? matchedWt.warranty_terms : '',
            }));
        } else if (name === 'taxNumber') {
            const upperVal = value.toUpperCase();
            setSupplierForm(prev => ({ ...prev, taxNumber: upperVal }));
            if (formErrors.taxNumber) {
                setFormErrors(prev => ({ ...prev, taxNumber: '' }));
            }
            const trimmed = upperVal.trim();
            if (trimmed.length === 15) {
                if (trimmed !== lastVerifiedGstRef.current) {
                    verifyGstDetails(trimmed);
                }
            } else {
                lastVerifiedGstRef.current = '';
            }
        } else {
            setSupplierForm(prev => ({ ...prev, [name]: value }));
        }

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
        if (formErrors.submit) {
            setFormErrors(prev => {
                const next = { ...prev };
                delete next.submit;
                return next;
            });
        }
    };

    // ── Save Supplier Form (POST /create/ or PUT /update/) ─────────────────────
    const handleSaveSupplier = async (e) => {
        e.preventDefault();
        const errors = validateSupplierForm(supplierForm, suppliers, editingSupplier, states);

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setIsSubmitting(true);
        const accessToken = localStorage.getItem('accessToken');
        const ptId = supplierForm.payment_terms
            ? Number(supplierForm.payment_terms)
            : (paymentTermsList.length > 0 ? paymentTermsList[0].payment_terms_id : 1);
        const wtId = Number(supplierForm.warranty_terms);

        const payload = {
            supplier_code: supplierForm.code.trim(),
            supplier_name: supplierForm.name.trim(),
            registration_number: (supplierForm.registrationNumber || supplierForm.taxNumber || '').trim(),
            tax_number: (supplierForm.taxNumber || '').trim(),
            address_line_1: supplierForm.address1.trim(),
            address_line_2: (supplierForm.address2 || '').trim(),
            city: supplierForm.city.trim(),
            postal_code: (supplierForm.postalCode || '').trim(),
            country_id: Number(supplierForm.country_id),
            state_id: supplierForm.state_id ? Number(supplierForm.state_id) : null,
            email: supplierForm.email.trim(),
            telephone: supplierForm.phone.trim(),
            website: (supplierForm.website || '').trim(),
            payment_terms: ptId,
            payment_terms_id: ptId,
            warranty_terms: wtId,
            warranty_terms_id: wtId,
            supplier_status: (supplierForm.supplierStatus || '').trim(),
            status: supplierForm.status ?? true,
        };

        try {
            if (editingSupplier) {
                // ── PUT /api/suppliers/{id}/update/ ──
                let res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/suppliers/${editingSupplier.id}/update/`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!res.ok && res.status === 400) {
                    const errData = await res.json().catch(() => ({}));
                    const fieldErrors = mapDrfErrorsToFields(errData);
                    if (Object.keys(fieldErrors).length > 0) {
                        setFormErrors(prev => ({ ...prev, ...fieldErrors }));
                    }
                    const errMsg = extractApiErrorMessage(errData, '');
                    if (errMsg.toLowerCase().includes('expected a list') || errMsg.toLowerCase().includes('not a list')) {
                        res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/suppliers/${editingSupplier.id}/update/`, {
                            method: 'PUT',
                            headers: {
                                'Authorization': `Bearer ${accessToken}`,
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify([payload]),
                        });
                    } else {
                        throw new Error(errMsg || `Update failed (${res.status})`);
                    }
                }

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    const fieldErrors = mapDrfErrorsToFields(errData);
                    if (Object.keys(fieldErrors).length > 0) {
                        setFormErrors(prev => ({ ...prev, ...fieldErrors }));
                    }
                    throw new Error(extractApiErrorMessage(errData, `Update failed (${res.status})`));
                }

                setAlert({ type: 'success', message: `Supplier "${supplierForm.name}" updated successfully!` });
            } else {
                // ── POST /api/suppliers/create/ ──
                // Sends [{ ... }] as requested by API specification
                let res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/suppliers/create/`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify([payload]),
                });

                // Fallback to single object if API endpoint expects dictionary
                if (!res.ok && res.status === 400) {
                    const errData = await res.json().catch(() => ({}));
                    const errMsg = extractApiErrorMessage(errData, '');
                    if (errMsg.toLowerCase().includes('expected a dictionary') || errMsg.toLowerCase().includes('got list')) {
                        res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/suppliers/create/`, {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${accessToken}`,
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(payload),
                        });
                    } else {
                        const fieldErrors = mapDrfErrorsToFields(errData);
                        if (Object.keys(fieldErrors).length > 0) {
                            setFormErrors(prev => ({ ...prev, ...fieldErrors }));
                        }
                        throw new Error(errMsg || `Creation failed (${res.status})`);
                    }
                }

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    const fieldErrors = mapDrfErrorsToFields(errData);
                    if (Object.keys(fieldErrors).length > 0) {
                        setFormErrors(prev => ({ ...prev, ...fieldErrors }));
                    }
                    throw new Error(extractApiErrorMessage(errData, `Creation failed (${res.status})`));
                }

                setAlert({ type: 'success', message: `Supplier "${supplierForm.name}" registered successfully!` });
            }

            setTimeout(() => setAlert(null), 3000);
            setShowSupplierModal(false);
            setEditingSupplier(null);
            setSupplierForm(EMPTY_SUPPLIER_FORM);
            await fetchSuppliers();
        } catch (error) {
            console.error('[SupplierList] Supplier form submit error:', error);
            const msg = error.message || 'Error saving supplier.';
            setFormErrors(prev => ({ ...prev, submit: msg }));
            setAlert({ type: 'error', message: msg });
            setTimeout(() => setAlert(null), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Delete Supplier Handler (DELETE /api/suppliers/{id}/delete/) ───────────
    const handleConfirmDelete = async () => {
        if (!pendingDelete) return;
        setIsSubmitting(true);
        const accessToken = localStorage.getItem('accessToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/suppliers/${pendingDelete.id}/delete/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Delete failed (${res.status})`));
            }

            setAlert({ type: 'success', message: `Supplier "${pendingDelete.name}" deleted successfully.` });
            setTimeout(() => setAlert(null), 3000);
            setPendingDelete(null);
            await fetchSuppliers();
        } catch (err) {
            console.error('[SupplierList] Delete supplier error:', err);
            setAlert({ type: 'error', message: err.message || 'Error deleting supplier.' });
            setTimeout(() => setAlert(null), 4000);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Tab 2: Contacts Management ────────────────────────────────────────────
    const handleOpenAddContactModal = (supplierId) => {
        setContactModalSupplierId(supplierId);
        setEditingContactId(null);
        setContactFormData(EMPTY_CONTACT_MODAL_FORM);
        setContactFormErrors({});
    };

    const handleOpenEditContactModal = (supplierId, contact) => {
        setContactModalSupplierId(supplierId);
        setEditingContactId(contact.id);
        setContactFormData({
            name: contact.name || '',
            designation: contact.designation || '',
            email: contact.email || '',
            phone: contact.phone || '',
            isPrimary: Boolean(contact.isPrimary),
            status: contact.status ?? true,
        });
        setContactFormErrors({});
    };

    const handleSaveContactModal = async (e) => {
        e.preventDefault();
        const errors = validateContactForm(contactFormData, contactModalSupplierId);

        if (Object.keys(errors).length > 0) {
            setContactFormErrors(errors);
            return;
        }

        setIsSubmitting(true);
        const accessToken = localStorage.getItem('accessToken');
        const payload = {
            supplier_id: Number(contactModalSupplierId),
            contact_name: contactFormData.name.trim(),
            contact_role: contactFormData.designation.trim() || null,
            email: contactFormData.email.trim(),
            telephone: contactFormData.phone.trim(),
            status: Boolean(contactFormData.status),
            is_primary: Boolean(contactFormData.isPrimary),
        };

        try {
            if (editingContactId) {
                // ── PUT /api/supplier-contacts/{id}/update/ ──
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-contacts/${editingContactId}/update/`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    const fieldErrors = mapDrfErrorsToFields(errData);
                    if (Object.keys(fieldErrors).length > 0) {
                        setContactFormErrors(prev => ({ ...prev, ...fieldErrors }));
                    }
                    throw new Error(extractApiErrorMessage(errData, `Contact update failed (${res.status})`));
                }

                setAlert({ type: 'success', message: `Contact "${contactFormData.name}" updated successfully.` });
            } else {
                // ── POST /api/supplier-contacts/create/ ──
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-contacts/create/`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    const fieldErrors = mapDrfErrorsToFields(errData);
                    if (Object.keys(fieldErrors).length > 0) {
                        setContactFormErrors(prev => ({ ...prev, ...fieldErrors }));
                    }
                    throw new Error(extractApiErrorMessage(errData, `Failed to add contact (${res.status})`));
                }

                setAlert({ type: 'success', message: `Contact "${contactFormData.name}" added successfully.` });
            }

            setTimeout(() => setAlert(null), 3000);
            const savedSupplierId = contactModalSupplierId;
            setContactModalSupplierId(null);
            setEditingContactId(null);
            setContactFormData(EMPTY_CONTACT_MODAL_FORM);
            await fetchSupplierContacts(savedSupplierId);
        } catch (err) {
            console.error('[SupplierList] Contact save error:', err);
            setAlert({ type: 'error', message: err.message || 'Error saving contact.' });
            setTimeout(() => setAlert(null), 4000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleContactStatus = async (supplierId, contactId, currentStatus) => {
        const nextStatus = !currentStatus;
        const accessToken = localStorage.getItem('accessToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-contacts/${contactId}/status/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: nextStatus })
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Status toggle failed (${res.status})`));
            }

            setContactsMap(prev => ({
                ...prev,
                [supplierId]: (prev[supplierId] || []).map(c => c.id === contactId ? { ...c, status: nextStatus } : c)
            }));
            setAlert({ type: 'success', message: 'Contact status updated.' });
            setTimeout(() => setAlert(null), 3000);
        } catch (err) {
            setAlert({ type: 'error', message: err.message || 'Failed to update contact status.' });
            setTimeout(() => setAlert(null), 4000);
        }
    };

    const handleDeleteContact = async (supplierId, contactId) => {
        const accessToken = localStorage.getItem('accessToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-contacts/${contactId}/delete/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Delete contact failed (${res.status})`));
            }

            setContactsMap(prev => ({
                ...prev,
                [supplierId]: (prev[supplierId] || []).filter(c => c.id !== contactId)
            }));
            setAlert({ type: 'success', message: 'Contact removed.' });
            setTimeout(() => setAlert(null), 3000);
        } catch (err) {
            setAlert({ type: 'error', message: err.message || 'Failed to delete contact.' });
            setTimeout(() => setAlert(null), 4000);
        }
    };

    // ── Tab 3: Documents Management (API: /api/supplier-documents/) ──────────
    const getDisplayFilename = (urlOrName) => {
        if (!urlOrName) return '—';
        let clean = String(urlOrName).replace(/[\r\n\t]+/g, '').trim();
        try {
            if (clean.startsWith('http://') || clean.startsWith('https://')) {
                const parsed = new URL(clean);
                const pathParts = parsed.pathname.split('/');
                const last = decodeURIComponent(pathParts[pathParts.length - 1] || '');
                if (last) return last;
            }
        } catch (_) { }
        const parts = clean.split('/');
        return parts[parts.length - 1] || clean;
    };

    const resolveDocumentUrl = (doc) => {
        if (!doc) return '';
        let raw = (
            doc.original_filename ||
            doc.originalFilename ||
            doc.file ||
            doc.file_url ||
            doc.document_file ||
            doc.url ||
            doc.attachment ||
            doc.document ||
            ''
        );
        if (raw && typeof raw === 'object' && raw.url) {
            raw = raw.url;
        }
        raw = String(raw).replace(/[\r\n\t]+/g, '').trim();

        // 1. If storage_key is an absolute URL
        let storageKey = String(doc.storage_key || '').replace(/[\r\n\t]+/g, '').trim();
        if (storageKey.startsWith('http://') || storageKey.startsWith('https://')) {
            return storageKey.replace(/\s+/g, '%20');
        }

        // 2. If raw is an absolute URL (SharePoint link or external CDN)
        if (raw.startsWith('http://') || raw.startsWith('https://')) {
            return raw.replace(/\s+/g, '%20');
        }

        const rawApiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
        const serverOrigin = rawApiBase.replace(/\/api\/?$/, '');

        // 3. If relative storage key exists (e.g. "supplier_documents/SUP-001/Supplier-api-document.pdf")
        const candidate = (storageKey && storageKey.includes('/')) ? storageKey : raw;
        if (candidate) {
            const cleanPath = candidate.replace(/^\/+/, '');
            if (cleanPath.startsWith('media/')) {
                return `${serverOrigin}/${cleanPath}`.replace(/\s+/g, '%20');
            }
            if (cleanPath.includes('/')) {
                return `${serverOrigin}/media/${cleanPath}`.replace(/\s+/g, '%20');
            }
            return `${serverOrigin}/media/supplier_documents/${cleanPath}`.replace(/\s+/g, '%20');
        }

        return '';
    };

    const formatUtcDisplay = (isoStr) => {
        if (!isoStr) return '—';
        try {
            const d = new Date(isoStr);
            if (isNaN(d.getTime())) return isoStr;
            return `${isoStr.replace('T', ' ').substring(0, 19)} UTC`;
        } catch {
            return isoStr;
        }
    };

    const getDocumentTypeName = (docOrId) => {
        if (!docOrId) return '—';
        if (typeof docOrId === 'string' && isNaN(Number(docOrId))) {
            return docOrId;
        }
        if (typeof docOrId === 'number' || (typeof docOrId === 'string' && !isNaN(Number(docOrId)))) {
            const found = documentTypes.find(dt => String(dt.id) === String(docOrId));
            return found ? found.name : `Type #${docOrId}`;
        }
        const doc = docOrId;
        if (doc.document_type_name) return doc.document_type_name;
        if (doc.document_type && typeof doc.document_type === 'string' && isNaN(Number(doc.document_type))) {
            return doc.document_type;
        }
        if (doc.document_type && typeof doc.document_type === 'object' && doc.document_type.name) {
            return doc.document_type.name;
        }
        const typeId = doc.document_type_id ?? doc.type ?? doc.document_type;
        if (typeId !== undefined && typeId !== null && typeId !== '') {
            const found = documentTypes.find(dt => String(dt.id) === String(typeId));
            if (found && found.name) return found.name;
            if (!isNaN(Number(typeId))) return `Type #${typeId}`;
            return String(typeId);
        }
        return '—';
    };

    const handleOpenAddDocModal = (supplierId) => {
        if (documentTypes.length === 0) {
            fetchDocumentTypes();
        }

        setDocModalSupplierId(supplierId);
        setEditingDocId(null);
        setSelectedDocFile(null);
        setDocFileError('');
        setDocFormErrors({});
        setDocFormData({
            supplier_id: supplierId ? String(supplierId) : '',
            document_type_id: '',
            document_name: '',
            effective_date: '',
            expiry_date: '',
            existingFilename: '',
        });
    };

    const handleOpenEditDocModal = (supplierId, doc) => {
        if (documentTypes.length === 0) {
            fetchDocumentTypes();
        }
        setDocModalSupplierId(supplierId);
        setEditingDocId(doc.id);
        setSelectedDocFile(null);
        setDocFileError('');
        setDocFormErrors({});

        let matchedId = '';
        if (doc.document_type_id) {
            const byId = documentTypes.find(dt => String(dt.id) === String(doc.document_type_id));
            matchedId = byId ? String(byId.id) : String(doc.document_type_id);
        } else if (doc.document_type) {
            const byName = documentTypes.find(dt => dt.name?.toLowerCase() === String(doc.document_type).toLowerCase());
            if (byName) matchedId = String(byName.id);
        } else if (doc.type) {
            const byType = documentTypes.find(dt =>
                String(dt.id) === String(doc.type) ||
                dt.name?.toLowerCase() === String(doc.type).toLowerCase()
            );
            if (byType) matchedId = String(byType.id);
        }

        if (!matchedId && documentTypes.length > 0) {
            matchedId = String(documentTypes[0].id);
        }

        setDocFormData({
            supplier_id: supplierId ? String(supplierId) : (doc.supplier_id ? String(doc.supplier_id) : ''),
            document_type_id: matchedId || '',
            document_name: doc.document_name || doc.name || '',
            effective_date: doc.effective_date || doc.effectiveDate || '',
            expiry_date: doc.expiry_date || doc.expiryDate || '',
            existingFilename: doc.original_filename || doc.originalFilename || '',
        });
    };

    const handleCloseDocModal = () => {
        setDocModalSupplierId(null);
        setEditingDocId(null);
        setSelectedDocFile(null);
        setDocFileError('');
        setDocFormErrors({});
        setDocFormData(EMPTY_DOC_MODAL_FORM);
    };

    const handleDocFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
        const ext = file.name.split('.').pop().toLowerCase();
        if (!allowed.includes(ext)) {
            const msg = 'Invalid file type. Allowed: PDF, DOC, DOCX, XLS, XLSX';
            setDocFileError(msg);
            setDocFormErrors(prev => ({ ...prev, original_filename: msg }));
            setSelectedDocFile(null);
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            const msg = 'File size exceeds maximum allowed 5 MB limit.';
            setDocFileError(msg);
            setDocFormErrors(prev => ({ ...prev, original_filename: msg }));
            setSelectedDocFile(null);
            return;
        }

        if (file.name.length > 2000) {
            const msg = 'Original filename cannot exceed 2000 characters.';
            setDocFileError(msg);
            setDocFormErrors(prev => ({ ...prev, original_filename: msg }));
            setSelectedDocFile(null);
            return;
        }

        setDocFileError('');
        setDocFormErrors(prev => {
            const next = { ...prev };
            delete next.original_filename;
            delete next.submit;
            return next;
        });
        setSelectedDocFile(file);
        if (!docFormData.document_name) {
            const autoName = file.name.replace(/\.[^/.]+$/, '').replace(/[^A-Za-z0-9 @&_-]/g, ' ').replace(/\s+/g, ' ').trim();
            if (autoName) {
                setDocFormData(prev => ({
                    ...prev,
                    document_name: autoName.charAt(0).toUpperCase() + autoName.slice(1)
                }));
            }
        }
    };

    const handleEffectiveDateChange = (val) => {
        setDocFormData(prev => ({ ...prev, effective_date: val }));
        setDocFormErrors(prev => {
            const next = { ...prev };
            delete next.submit;

            if (!val) {
                next.effective_date = 'Effective date is required.';
            } else if (!isValidDate(val)) {
                next.effective_date = 'Enter a valid effective date.';
            } else {
                delete next.effective_date;
            }

            // Revalidate Expiry Date whenever Effective Date changes
            const currentExpiry = docFormData.expiry_date;
            if (currentExpiry) {
                if (!isValidDate(currentExpiry)) {
                    next.expiry_date = 'Enter a valid expiry date.';
                } else if (isValidDate(val) && currentExpiry <= val) {
                    next.expiry_date = 'Expiry date must be greater than effective date.';
                } else {
                    delete next.expiry_date;
                }
            }

            return next;
        });
    };

    const handleExpiryDateChange = (val) => {
        setDocFormData(prev => ({ ...prev, expiry_date: val }));
        setDocFormErrors(prev => {
            const next = { ...prev };
            delete next.submit;

            if (!val) {
                next.expiry_date = 'Expiry date is required.';
            } else if (!isValidDate(val)) {
                next.expiry_date = 'Enter a valid expiry date.';
            } else if (docFormData.effective_date && isValidDate(docFormData.effective_date) && val <= docFormData.effective_date) {
                next.expiry_date = 'Expiry date must be greater than effective date.';
            } else {
                delete next.expiry_date;
            }

            return next;
        });
    };

    const handleSaveDocModal = async (e) => {
        e.preventDefault();
        const errors = {};

        // 1. Supplier
        const targetSupplierId = docFormData.supplier_id || docModalSupplierId;
        if (!targetSupplierId) {
            errors.supplier_id = 'Please select a supplier.';
        } else {
            const matchedSup = (suppliers || []).find(s => String(s.id) === String(targetSupplierId));
            if (matchedSup && parseStatus(matchedSup.status) === false) {
                errors.supplier_id = 'Please select an active supplier.';
            }
        }

        // 2. Document Type
        if (!String(docFormData.document_type_id || '').trim()) {
            errors.document_type_id = 'Please select a document type.';
        } else {
            const matchedDt = (documentTypes || []).find(dt => String(dt.id) === String(docFormData.document_type_id));
            if (matchedDt && matchedDt.status === false) {
                errors.document_type_id = 'Please select an active document type.';
            }
        }

        // 3. Document Name
        const docNameErr = validateDocumentName(docFormData.document_name, { required: true });
        if (docNameErr) {
            errors.document_name = docNameErr;
        }

        // 4. File / Original Filename
        if (!editingDocId && !selectedDocFile && !docFormData.existingFilename) {
            errors.original_filename = 'Original filename is required.';
        } else if (selectedDocFile) {
            if (selectedDocFile.name.length > 2000) {
                errors.original_filename = 'Original filename cannot exceed 2000 characters.';
            } else {
                const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
                const ext = selectedDocFile.name.split('.').pop().toLowerCase();
                if (!allowed.includes(ext)) {
                    errors.original_filename = 'Invalid file type. Allowed: PDF, DOC, DOCX, XLS, XLSX';
                } else if (selectedDocFile.size > 5 * 1024 * 1024) {
                    errors.original_filename = 'File size exceeds maximum allowed 5 MB limit.';
                }
            }
        }

        // 5. Date Validation (Effective Date & Expiry Date)
        const dateErr = validateDocumentDates(docFormData.effective_date, docFormData.expiry_date);
        if (dateErr) {
            errors[dateErr.field] = dateErr.message;
        }

        if (Object.keys(errors).length > 0) {
            setDocFormErrors(errors);
            return;
        }

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
            setAlert({ type: 'error', message: 'Authentication required. Please log in again.' });
            if (setView) setView('signin');
            return;
        }

        setIsDocSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('supplier_id', String(targetSupplierId));
            formData.append('document_type_id', String(docFormData.document_type_id).trim());
            formData.append('document_name', docFormData.document_name.trim());
            formData.append('effective_date', docFormData.effective_date);
            formData.append('expiry_date', docFormData.expiry_date);
            if (selectedDocFile) {
                formData.append('file', selectedDocFile);
                formData.append('original_filename', selectedDocFile.name);
            } else if (docFormData.existingFilename) {
                formData.append('original_filename', docFormData.existingFilename);
            }

            const url = editingDocId
                ? `${process.env.REACT_APP_API_BASE_URL}/supplier-documents/${editingDocId}/update/`
                : `${process.env.REACT_APP_API_BASE_URL}/supplier-documents/create/`;

            const method = editingDocId ? 'PATCH' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                body: formData
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                const fieldErrors = mapDrfErrorsToFields(errData);
                if (Object.keys(fieldErrors).length > 0) {
                    setDocFormErrors(prev => ({ ...prev, ...fieldErrors }));
                }
                throw new Error(extractApiErrorMessage(errData, `Failed to ${editingDocId ? 'update' : 'upload'} document (${res.status})`));
            }

            await fetchSupplierDocuments(targetSupplierId);

            setAlert({
                type: 'success',
                message: `Document "${docFormData.document_name.trim()}" ${editingDocId ? 'updated' : 'uploaded'} successfully!`
            });
            setTimeout(() => setAlert(null), 3000);

            handleCloseDocModal();
        } catch (err) {
            console.error('[SupplierList] Document save error:', err);
            const msg = err.message || 'Failed to save document.';
            setDocFormErrors(prev => ({ ...prev, submit: msg }));
            setAlert({ type: 'error', message: msg });
            setTimeout(() => setAlert(null), 4000);
        } finally {
            setIsDocSubmitting(false);
        }
    };

    const handleToggleDocStatus = async (supplierId, docId, currentStatus) => {
        const nextStatus = !currentStatus;
        const accessToken = localStorage.getItem('accessToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-documents/${docId}/status/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: nextStatus })
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Status toggle failed (${res.status})`));
            }

            setDocumentsMap(prev => ({
                ...prev,
                [supplierId]: (prev[supplierId] || []).map(d => d.id === docId ? { ...d, status: nextStatus } : d)
            }));
            setAlert({ type: 'success', message: 'Document status updated.' });
            setTimeout(() => setAlert(null), 3000);
        } catch (err) {
            console.error('[SupplierList] Document status toggle error:', err);
            setAlert({ type: 'error', message: err.message || 'Failed to update document status.' });
            setTimeout(() => setAlert(null), 4000);
        }
    };

    const handleDeleteDocument = async (supplierId, docId) => {
        const accessToken = localStorage.getItem('accessToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-documents/${docId}/delete/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Delete document failed (${res.status})`));
            }

            setDocumentsMap(prev => ({
                ...prev,
                [supplierId]: (prev[supplierId] || []).filter(d => d.id !== docId)
            }));
            setAlert({ type: 'success', message: 'Document deleted successfully.' });
            setTimeout(() => setAlert(null), 3000);
        } catch (err) {
            console.error('[SupplierList] Delete document error:', err);
            setAlert({ type: 'error', message: err.message || 'Failed to delete document.' });
            setTimeout(() => setAlert(null), 4000);
        }
    };

    const handleViewDocument = (supplier, doc) => {
        setPreviewingDoc({ supplier, doc });
    };



    const triggerBrowserDownload = (downloadUrl, fileName) => {
        // Method 1: Hidden iframe
        try {
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.top = '-9999px';
            iframe.style.left = '-9999px';
            iframe.style.width = '1px';
            iframe.style.height = '1px';
            iframe.style.opacity = '0';
            iframe.style.border = 'none';
            iframe.src = downloadUrl;
            document.body.appendChild(iframe);
            setTimeout(() => {
                try {
                    document.body.removeChild(iframe);
                } catch (_) { }
            }, 60000);
        } catch (iframeErr) {
            console.warn('[SupplierList] Iframe download error:', iframeErr);
        }

        // Method 2: Anchor download stream
        try {
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = downloadUrl;
            if (fileName) {
                a.setAttribute('download', fileName);
            }
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                try {
                    document.body.removeChild(a);
                } catch (_) { }
            }, 2000);
        } catch (anchorErr) {
            console.warn('[SupplierList] Anchor download error:', anchorErr);
        }
    };

    const saveBlobAsFile = (blob, fileName) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            try {
                document.body.removeChild(a);
            } catch (_) { }
            window.URL.revokeObjectURL(blobUrl);
        }, 2000);
    };

    // ── Download Document (API: GET /api/supplier-documents/{id}/download/) ───
    const handleDownloadDocument = async (doc) => {
        if (!doc) {
            setAlert({ type: 'error', message: 'No document specified for download.' });
            setTimeout(() => setAlert(null), 3000);
            return;
        }

        const docId = doc.supplier_document_id ?? doc.id;
        const initialFileName = getDisplayFilename(
            doc.original_filename || doc.originalFilename || doc.document_name || doc.name || 'document.pdf'
        );

        if (!docId) {
            const fallbackUrl = resolveDocumentUrl(doc);
            if (fallbackUrl) {
                triggerBrowserDownload(fallbackUrl, initialFileName);
                return;
            }
            setAlert({ type: 'error', message: 'Document ID is missing. Cannot download file.' });
            setTimeout(() => setAlert(null), 3000);
            return;
        }

        setDownloadingDocId(docId);
        setAlert({ type: 'success', message: `Downloading "${initialFileName}"...` });

        try {
            const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
            const downloadApiUrl = `${apiBase}/supplier-documents/${docId}/download/`;
            const accessToken = localStorage.getItem('accessToken');

            const headers = {};
            if (accessToken) {
                headers['Authorization'] = `Bearer ${accessToken}`;
            }

            const res = await fetch(downloadApiUrl, {
                method: 'GET',
                headers,
            });

            if (!res.ok) {
                if (res.status === 401) {
                    throw new Error('Authentication required. Please log in again.');
                }
                if (res.status === 404) {
                    throw new Error('Document file not found on server (404).');
                }
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Download failed with status ${res.status}`));
            }

            const contentType = (res.headers.get('content-type') || '').toLowerCase();

            // Case A: JSON response with presigned URL or direct link
            if (contentType.includes('application/json')) {
                const data = await res.json().catch(() => ({}));
                const fileUrl = data.download_url || data.file_url || data.url || data?.data?.download_url || data?.data?.url;
                if (fileUrl) {
                    if (fileUrl.includes('sharepoint.com')) {
                        let spUrl = fileUrl;
                        if (!spUrl.includes('download=')) {
                            spUrl += (spUrl.includes('?') ? '&' : '?') + 'download=1';
                        }
                        triggerBrowserDownload(spUrl, initialFileName);
                    } else {
                        try {
                            const directRes = await fetch(fileUrl);
                            if (directRes.ok) {
                                const directBlob = await directRes.blob();
                                saveBlobAsFile(directBlob, initialFileName);
                            } else {
                                triggerBrowserDownload(fileUrl, initialFileName);
                            }
                        } catch (_) {
                            triggerBrowserDownload(fileUrl, initialFileName);
                        }
                    }
                    setAlert({ type: 'success', message: `Document "${initialFileName}" downloaded successfully!` });
                    setTimeout(() => setAlert(null), 3500);
                    return;
                }
                throw new Error(data.message || data.error || 'No valid download URL returned by server.');
            }

            // Case B: Direct binary stream / Blob file
            const blob = await res.blob();

            // Extract filename from Content-Disposition header if available
            let finalFileName = initialFileName;
            const disposition = res.headers.get('content-disposition');
            if (disposition) {
                const utfMatches = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
                if (utfMatches && utfMatches[1]) {
                    finalFileName = decodeURIComponent(utfMatches[1]);
                } else {
                    const standardMatches = /filename=["']?([^"';\n]+)["']?/i.exec(disposition);
                    if (standardMatches && standardMatches[1]) {
                        finalFileName = standardMatches[1].trim();
                    }
                }
            }
            if (!finalFileName.includes('.') && initialFileName.includes('.')) {
                const ext = initialFileName.split('.').pop();
                if (ext) finalFileName = `${finalFileName}.${ext}`;
            }

            saveBlobAsFile(blob, finalFileName);
            setAlert({ type: 'success', message: `Document "${finalFileName}" downloaded successfully!` });
            setTimeout(() => setAlert(null), 3500);

        } catch (err) {
            console.error(`[SupplierList] Document download error via API (${docId}):`, err);
            // Fallback to static URL if available
            const fallbackUrl = resolveDocumentUrl(doc);
            if (fallbackUrl) {
                console.log('[SupplierList] Falling back to static URL download:', fallbackUrl);
                triggerBrowserDownload(fallbackUrl, initialFileName);
                setAlert({ type: 'success', message: `Downloading "${initialFileName}"...` });
                setTimeout(() => setAlert(null), 3500);
            } else {
                setAlert({ type: 'error', message: err.message || 'Failed to download document.' });
                setTimeout(() => setAlert(null), 4000);
            }
        } finally {
            setDownloadingDocId(null);
        }
    };

    // ── Tab 4: Components Sourcing Management ─────────────────────────────────
    const handleOpenAddComponent = (supplierId) => {
        setCompModalSupplierId(supplierId);
        setEditingCompId(null);
        setCompFormData(EMPTY_COMP_FORM);
        setCompFormErrors({});
        if (catalogComponents.length === 0) fetchCatalogComponents();
        if (paymentTermsList.length === 0) fetchPaymentTerms();
        if (warrantyTermsList.length === 0) fetchWarrantyTerms();
        if (!componentsMap[supplierId]) {
            fetchSupplierComponents(supplierId);
        }
    };

    const handleOpenEditComponent = (supplierId, comp) => {
        setCompModalSupplierId(supplierId);
        setEditingCompId(comp.id);

        let ptVal = comp.payment_terms ?? comp.payment_terms_id ?? '';
        if (!ptVal && comp.payment_terms_name && paymentTermsList.length > 0) {
            const foundPt = paymentTermsList.find(p => p.payment_terms_name.toLowerCase() === comp.payment_terms_name.toLowerCase());
            if (foundPt) ptVal = foundPt.payment_terms_id;
        }

        let wtVal = comp.warranty_terms ?? comp.warranty_terms_id ?? '';
        if (!wtVal && comp.warranty_terms_name && warrantyTermsList.length > 0) {
            const foundWt = warrantyTermsList.find(w => w.warranty_terms.toLowerCase() === comp.warranty_terms_name.toLowerCase());
            if (foundWt) wtVal = foundWt.warranty_terms_id;
        }

        const matchedCat = catalogComponents.find(c => String(c.id) === String(comp.component_id) || (c.code && comp.code && c.code.toLowerCase() === comp.code.toLowerCase()));
        const compHsn = comp.hsn_code || (matchedCat ? matchedCat.hsn_code : '');

        setCompFormData({
            component_id: comp.component_id || '',
            code: comp.code || '',
            hsn_code: compHsn || '',
            name: comp.name || '',
            category: comp.category || '',
            supplier_part_number: comp.supplier_part_number || '',
            price: comp.price ? formatTwoDecimals(comp.price) : '',
            tax: comp.tax ? formatTwoDecimals(comp.tax) : '18.00',
            currency_code: comp.currency_code || 'INR',
            minimum_order_quantity: comp.minimum_order_quantity != null ? formatTwoDecimals(comp.minimum_order_quantity) : '10.00',
            lead_time_days: comp.lead_time_days ?? '15',
            payment_terms: ptVal,
            warranty_terms: wtVal,
            is_preferred: Boolean(comp.is_preferred),
            price_valid_until: comp.price_valid_until || '',
            status: comp.status ?? true,
        });
        setCompFormErrors({});
        if (catalogComponents.length === 0) fetchCatalogComponents();
        if (paymentTermsList.length === 0) fetchPaymentTerms();
        if (warrantyTermsList.length === 0) fetchWarrantyTerms();
    };

    const handleSelectCatalogComponent = (compIdentifier) => {
        const found = catalogComponents.find(c => String(c.id) === String(compIdentifier) || c.code === compIdentifier);

        // Check if this component is already mapped to this supplier
        const existing = (componentsMap[compModalSupplierId] || []).filter(
            sc => !editingCompId || String(sc.id) !== String(editingCompId)
        );
        const isDuplicate = found && existing.some(
            sc => String(sc.component_id) === String(found.id) || (sc.code && found.code && sc.code.toLowerCase() === found.code.toLowerCase())
        );

        if (isDuplicate) {
            setCompFormErrors(prev => ({
                ...prev,
                component_id: `"${found.name || found.code}" is already mapped to this supplier. Please select a different component.`
            }));
        } else {
            setCompFormErrors(prev => {
                const next = { ...prev };
                delete next.component_id;
                return next;
            });
        }

        if (found) {
            const cleanCode = sanitizePartNumber(found.code);
            const autoPartNumber = cleanCode ? `${cleanCode}-SUP` : 'PART-001';

            setCompFormData(prev => {
                const prevPart = (prev.supplier_part_number || '').trim();
                const shouldReplace = !prevPart || prevPart.endsWith('-SUP') || !/^[A-Za-z0-9@&_-]+$/.test(prevPart);
                return {
                    ...prev,
                    component_id: found.id,
                    code: found.code,
                    hsn_code: found.hsn_code || '',
                    name: found.name,
                    category: found.category,
                    supplier_part_number: shouldReplace ? autoPartNumber : prevPart,
                };
            });
        } else {
            setCompFormData(prev => ({ ...prev, component_id: '', code: compIdentifier, hsn_code: '', supplier_part_number: '' }));
        }
    };

    const handleSaveComponentMapping = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!compFormData.component_id && !compFormData.code.trim()) {
            errors.component_id = 'Component is required.';
        }
        if (!compFormData.price || isNaN(parseFloat(compFormData.price))) {
            errors.price = 'Valid price is required.';
        }
        if (!compFormData.warranty_terms) {
            errors.warranty_terms = 'Please select warranty terms.';
        }

        // Supplier Part Number validation & sanitization
        let partNo = (compFormData.supplier_part_number || '').trim();
        if (!partNo) {
            const cleanCode = sanitizePartNumber(compFormData.code);
            partNo = cleanCode ? `${cleanCode}-SUP` : 'PART-001';
        }

        const partErr = validatePartNumber(partNo, { required: true, fieldName: 'Supplier part number' });
        if (partErr) {
            errors.supplier_part_number = partErr;
        }

        if (Object.keys(errors).length > 0) {
            setCompFormErrors(errors);
            return;
        }

        // Determine valid component_id from selected or matched catalog
        let resolvedCompId = compFormData.component_id;
        if (!resolvedCompId) {
            const match = catalogComponents.find(c => c.code.toLowerCase() === compFormData.code.trim().toLowerCase());
            if (match) resolvedCompId = match.id;
        }

        if (!resolvedCompId) {
            setCompFormErrors(prev => ({ ...prev, component_id: 'Please select an existing Design Component from the catalog.' }));
            setAlert({ type: 'error', message: 'Please select an existing Design Component from the catalog.' });
            setTimeout(() => setAlert(null), 4000);
            return;
        }

        // Duplicate Check: Check if component is already assigned to this supplier
        const existing = (componentsMap[compModalSupplierId] || []).filter(
            sc => !editingCompId || String(sc.id) !== String(editingCompId)
        );
        const duplicate = existing.find(
            sc => String(sc.component_id) === String(resolvedCompId) || (sc.code && compFormData.code && sc.code.toLowerCase() === compFormData.code.trim().toLowerCase())
        );

        if (duplicate) {
            const compTitle = compFormData.name || compFormData.code || 'This component';
            const errorMsg = `"${compTitle}" is already mapped to this supplier. Duplicate component mappings are not allowed.`;
            setCompFormErrors(prev => ({ ...prev, component_id: errorMsg }));
            setAlert({ type: 'error', message: errorMsg });
            setTimeout(() => setAlert(null), 5000);
            return;
        }

        setIsSubmitting(true);
        const accessToken = localStorage.getItem('accessToken');
        const payload = {
            supplier_id: compModalSupplierId,
            component_id: parseInt(resolvedCompId, 10),
            ...(compFormData.hsn_code ? { hsn_code: compFormData.hsn_code.trim() } : {}),
            supplier_part_number: partNo,
            price: parseFloat(compFormData.price) || 0,
            tax: parseFloat(compFormData.tax) || 0,
            currency_code: compFormData.currency_code || 'INR',
            minimum_order_quantity: parseFloat(compFormData.minimum_order_quantity) || 1,
            lead_time_days: parseInt(compFormData.lead_time_days, 10) || 0,
            warranty_terms_id: Number(compFormData.warranty_terms),
            is_preferred: Boolean(compFormData.is_preferred),
            price_valid_until: compFormData.price_valid_until || null,
            status: compFormData.status !== undefined ? Boolean(compFormData.status) : true,
        };

        try {
            if (editingCompId) {
                // ── PUT /api/supplier-components/{id}/update/ ──
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-components/${editingCompId}/update/`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    const fieldErrors = mapDrfErrorsToFields(errData);
                    if (Object.keys(fieldErrors).length > 0) {
                        setCompFormErrors(prev => ({ ...prev, ...fieldErrors }));
                    }
                    throw new Error(extractApiErrorMessage(errData, `Component mapping update failed (${res.status})`));
                }

                setAlert({ type: 'success', message: `Component "${compFormData.name || compFormData.code}" updated successfully.` });
            } else {
                // ── POST /api/supplier-components/create/ ──
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-components/create/`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    const fieldErrors = mapDrfErrorsToFields(errData);
                    if (Object.keys(fieldErrors).length > 0) {
                        setCompFormErrors(prev => ({ ...prev, ...fieldErrors }));
                    }
                    throw new Error(extractApiErrorMessage(errData, `Failed to map component (${res.status})`));
                }

                setAlert({ type: 'success', message: `Component "${compFormData.name || compFormData.code}" mapped successfully.` });
            }

            setTimeout(() => setAlert(null), 3000);
            const savedSupplierId = compModalSupplierId;
            setCompModalSupplierId(null);
            setEditingCompId(null);
            setCompFormData(EMPTY_COMP_FORM);
            await fetchSupplierComponents(savedSupplierId);
        } catch (err) {
            console.error('[SupplierList] Component mapping save error:', err);
            const rawMsg = err.message || 'Error saving component mapping.';
            let friendlyMsg = rawMsg;
            if (rawMsg.toLowerCase().includes('unique') || rawMsg.toLowerCase().includes('already exists') || rawMsg.toLowerCase().includes('duplicate')) {
                friendlyMsg = `"${compFormData.name || compFormData.code || 'This component'}" is already mapped to this supplier. Duplicate component mappings are not allowed.`;
                setCompFormErrors(prev => ({ ...prev, component_id: friendlyMsg, submit: friendlyMsg }));
            } else {
                setCompFormErrors(prev => ({ ...prev, submit: friendlyMsg }));
            }
            setAlert({ type: 'error', message: friendlyMsg });
            setTimeout(() => setAlert(null), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleCompStatus = async (supplierId, compMapId, currentStatus) => {
        const nextStatus = !currentStatus;
        const accessToken = localStorage.getItem('accessToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-components/${compMapId}/status/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: nextStatus })
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Component status update failed (${res.status})`));
            }

            setComponentsMap(prev => ({
                ...prev,
                [supplierId]: (prev[supplierId] || []).map(c => c.id === compMapId ? { ...c, status: nextStatus } : c)
            }));
            setAlert({ type: 'success', message: 'Component status updated.' });
            setTimeout(() => setAlert(null), 3000);
        } catch (err) {
            setAlert({ type: 'error', message: err.message || 'Failed to update component status.' });
            setTimeout(() => setAlert(null), 4000);
        }
    };

    const handleDeleteComponentMapping = async (supplierId, compMapId) => {
        const accessToken = localStorage.getItem('accessToken');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/supplier-components/${compMapId}/delete/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Unlink failed (${res.status})`));
            }

            setComponentsMap(prev => ({
                ...prev,
                [supplierId]: (prev[supplierId] || []).filter(c => c.id !== compMapId)
            }));
            setAlert({ type: 'success', message: 'Component mapping removed.' });
            setTimeout(() => setAlert(null), 3000);
        } catch (err) {
            setAlert({ type: 'error', message: err.message || 'Failed to unlink component.' });
            setTimeout(() => setAlert(null), 4000);
        }
    };

    // Calculate calculated total for component modal preview
    const calcPrice = parseFloat(compFormData.price) || 0;
    const calcTax = parseFloat(compFormData.tax) || 0;
    const calcTotal = (calcPrice + (calcPrice * calcTax / 100)).toFixed(2);

    // Active suppliers and active document types for Document Modal
    const activeSuppliers = (suppliers || []).filter(s =>
        parseStatus(s.status) !== false || String(s.id) === String(docFormData.supplier_id || docModalSupplierId)
    );
    const activeDocTypes = (documentTypes || []).filter(dt =>
        dt.status !== false || String(dt.id) === String(docFormData.document_type_id)
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="assets-root"
        >
            {/* ══ 1. MAIN HEADER ══════════════════════════════════════════════════ */}
            <InnerHeader
                user={user}
                setView={setView}
                onProfilePhotoSuccess={async () => {
                    setTimeout(async () => { await fetchUserDetails(); }, 500);
                    setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                }}
            />

            {/* ══ 2. SECONDARY BAR ════════════════════════════════════════════════ */}
            <div className="assets-secondary-bar">
                <div className="assets-title-wrap">
                    <div className="assets-plus-icon">
                        <Plus size={13} />
                    </div>
                    <span className="assets-page-title">SUPPLIER</span>
                </div>
                <div className="assets-top-nav">
                    {/* Navigation buttons commented out as requested - preserved for future use
                    {['Home', 'Dashboard', 'Analytics', 'Export Reports', 'Asset', 'Supplier', 'Pole Design', 'Maintenance']
                        .filter(label => {
                            if (label === 'Home') return true;
                            const role = user?.role;
                            const normRole = role ? role.replace(/[-_ ]/g, '').toLowerCase() : '';
                            if (label === 'Analytics' && normRole !== 'superadmin') return false;
                            if (normRole === 'technicalsupport') return true;
                            if (normRole === 'superadmin') return true;
                            if (normRole === 'government') return label === 'Dashboard';
                            if (normRole === 'maintenance' || normRole === 'research') {
                                return label === 'Dashboard' || label === 'Maintenance';
                            }
                            if (normRole === 'assetmanagement') {
                                return label !== 'Maintenance' && label !== 'Export Reports';
                            }
                            return true;
                        })
                        .map(label => (
                            <button
                                key={label}
                                className={`assets-top-nav-btn${activeTopNav === label ? ' active' : ''}`}
                                disabled={label === 'Export Reports'}
                                style={label === 'Export Reports' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                onClick={() => {
                                    setActiveTopNav(label);
                                    if (label === 'Home') setView && setView('home');
                                    else if (label === 'Dashboard') setView && setView('dashboard');
                                    else if (label === 'Analytics') setView && setView('analytics');
                                    else if (label === 'Asset') setView && setView('assets-list');
                                    else if (label === 'Supplier') setView && setView('supplier-list');
                                    else if (label === 'Pole Design') setView && setView('pole-designs');
                                    else if (label === 'Maintenance') setView && setView('maintenance');
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    */}
                    <button
                        className="assets-top-nav-btn"
                        onClick={() => setView && setView('home')}
                        title="Back to Home"
                    >
                        ← Home
                    </button>
                </div>
            </div>

            {/* ══ 3. BODY ═════════════════════════════════════════════════════════ */}
            <div className="assets-body">
                {/* Main Content Area */}
                <div className="assets-main">
                    {/* Alert Banner */}
                    {alert && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`assets-alert ${alert.type}`}
                        >
                            {alert.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
                            <span className="alert-message-flex">{alert.message}</span>
                            <button className="assets-alert-close" onClick={() => setAlert(null)}>×</button>
                        </motion.div>
                    )}

                    {/* ── SUPPLIER LIST TABLE CARD ──────────────────────────────── */}
                    <div className="comp-card">
                        <div className="comp-header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <h1 className="comp-page-title">Supplier List</h1>
                                <p className="comp-page-subtitle">
                                    Manage approved suppliers, authorized contacts, and hardware component sourcing.
                                </p>
                            </div>
                            <button
                                className="sup-topbar-action-btn"
                                onClick={() => fetchSuppliers()}
                                title="Refresh Suppliers from Server"
                                style={{ padding: '8px 14px', marginTop: '4px' }}
                            >
                                <RefreshCw size={13} className={isLoading ? 'spin-icon' : ''} />
                                <span>Refresh</span>
                            </button>
                        </div>

                        {/* Toolbar */}
                        <div className="comp-toolbar">
                            <div className="comp-search-wrap">
                                <Search size={14} color="#64748b" />
                                <input
                                    className="comp-search-input"
                                    placeholder="Search Suppliers by code, name, country, email..."
                                    value={searchQuery}
                                    onChange={e => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <button className="comp-add-btn" onClick={handleOpenAddSupplier}>
                                <Plus size={14} /> Add New Supplier
                            </button>
                        </div>

                        {/* Table */}
                        <div className="comp-table-wrap">
                            <table className="comp-table">
                                <thead>
                                    <tr>
                                        <th
                                            className="comp-th sortable comp-th-expand"
                                            onClick={() => handleSort('id')}
                                        >
                                            ID
                                            <ChevronsUpDown size={12} style={{ display: 'inline', marginLeft: 4, verticalAlign: 'middle' }} />
                                        </th>
                                        <th
                                            className="comp-th sortable"
                                            onClick={() => handleSort('code')}
                                        >
                                            Supplier Code
                                            {sortField === 'code' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                        </th>
                                        <th
                                            className="comp-th sortable"
                                            onClick={() => handleSort('name')}
                                        >
                                            Supplier Name
                                            {sortField === 'name' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                        </th>
                                        <th
                                            className="comp-th sortable"
                                            onClick={() => handleSort('country')}
                                        >
                                            Country
                                            {sortField === 'country' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                        </th>
                                        <th
                                            className="comp-th sortable"
                                            onClick={() => handleSort('email')}
                                        >
                                            Email
                                            {sortField === 'email' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                        </th>
                                        <th
                                            className="comp-th sortable"
                                            onClick={() => handleSort('phone')}
                                        >
                                            Telephone
                                            {sortField === 'phone' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                        </th>
                                        <th
                                            className="comp-th sortable"
                                            onClick={() => handleSort('paymentTerms')}
                                        >
                                            Payment Terms
                                            {sortField === 'paymentTerms' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                        </th>
                                        <th className="comp-th comp-th-status">Status</th>
                                        <th className="comp-th comp-th-action">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#00bcff' }}>
                                                    <Loader2 size={20} className="spin-icon" />
                                                    <span>Loading suppliers from server...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : paginated.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="assets-td assets-table-empty">
                                                No suppliers found. Click "Add New Supplier" to create one.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginated.map(item => {
                                            const isExpanded = Boolean(expandedRows[item.id]);
                                            const activeTab = activeSubTabs[item.id] || 'details';
                                            const contactsList = contactsMap[item.id] || [];
                                            const componentsList = componentsMap[item.id] || [];
                                            const docsList = documentsMap[item.id] || item.documents || [];
                                            const contactsCount = contactsList.length;
                                            const docsCount = docsList.length;
                                            const compCount = componentsList.length;
                                            const subTabLoading = loadingSubTabs[item.id] || {};

                                            return (
                                                <React.Fragment key={item.id}>
                                                    {/* Main Row */}
                                                    <tr className={`comp-table-row${isExpanded ? ' expanded-highlight' : ''}`}>
                                                        {/* ID + Expand Toggle */}
                                                        <td className="comp-td">
                                                            <button
                                                                className={`comp-expand-btn${isExpanded ? ' open' : ''}`}
                                                                onClick={() => toggleRowExpand(item.id)}
                                                                title="Expand Supplier Details & Sub-tabs"
                                                            >
                                                                <ChevronRight size={14} />
                                                            </button>
                                                            <span>{item.id}</span>
                                                        </td>

                                                        {/* Supplier Code */}
                                                        <td className="comp-td">
                                                            <span className="comp-code-pill">{item.code}</span>
                                                        </td>

                                                        {/* Supplier Name */}
                                                        <td className="comp-td" style={{ fontWeight: 600, color: '#f8fafc' }}>
                                                            {item.name}
                                                        </td>

                                                        {/* Country */}
                                                        <td className="comp-td">
                                                            <span className="sup-country-badge">{getCountryLabel(item)}</span>
                                                        </td>

                                                        {/* Email */}
                                                        <td className="comp-td" style={{ color: '#94a3b8' }}>
                                                            {item.email}
                                                        </td>

                                                        {/* Telephone */}
                                                        <td className="comp-td" style={{ color: '#cbd5e1' }}>
                                                            {item.phone}
                                                        </td>

                                                        {/* Payment Terms */}
                                                        <td className="comp-td">
                                                            <span style={{ color: '#00bcff', fontWeight: 500 }}>
                                                                {getPaymentTermsLabel(item)}
                                                            </span>
                                                        </td>

                                                        {/* Status Switch (PATCH /api/suppliers/{id}/status/) */}
                                                        <td className="comp-td comp-td-status">
                                                            <button
                                                                className={`comp-toggle-btn ${item.status ? 'on' : ''}`}
                                                                onClick={e => handleToggleStatus(item.id, item.status, e)}
                                                                title={item.status ? 'Active' : 'Inactive'}
                                                            >
                                                                <span className="comp-toggle-thumb" />
                                                            </button>
                                                        </td>

                                                        {/* Actions */}
                                                        <td className="comp-td comp-td-action">
                                                            <span className="assets-action-group">
                                                                <button
                                                                    className="assets-action-btn edit"
                                                                    title="Edit Supplier"
                                                                    onClick={() => handleOpenEditSupplier(item)}
                                                                >
                                                                    <Edit2 size={14} />
                                                                </button>
                                                                <button
                                                                    className="assets-action-btn delete"
                                                                    title="Delete Supplier"
                                                                    onClick={() => setPendingDelete(item)}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </span>
                                                        </td>
                                                    </tr>

                                                    {/* ── EXPANDED MULTI-TAB CARD ──────────────────────────── */}
                                                    {isExpanded && (
                                                        <tr className="comp-detail-row">
                                                            <td colSpan={9} style={{ padding: '8px 12px !important' }}>
                                                                <div className="sup-expanded-wrapper">
                                                                    {/* Top Header of Expanded Block */}
                                                                    <div className="sup-expanded-topbar">
                                                                        <div className="sup-topbar-left">
                                                                            <span className="sup-code-tag">{item.code}</span>
                                                                            <h3 className="sup-name-heading">{item.name}</h3>
                                                                            <span className="sup-country-badge">{getCountryLabel(item)}</span>
                                                                            {item.supplierStatus && (
                                                                                <span style={{ fontSize: '11px', color: '#00bcff', background: 'rgba(0,188,255,0.08)', padding: '2px 8px', borderRadius: '4px', border: '1px solid rgba(0,188,255,0.2)' }}>
                                                                                    {item.supplierStatus}
                                                                                </span>
                                                                            )}
                                                                        </div>

                                                                        <div className="sup-topbar-right">
                                                                            <span className={`sup-status-badge ${item.status ? 'active' : 'inactive'}`}>
                                                                                <span className="sup-status-dot" />
                                                                                {item.status ? 'Active Supplier' : 'Inactive'}
                                                                            </span>

                                                                            <button
                                                                                className="sup-topbar-action-btn"
                                                                                title="Edit Supplier Profile"
                                                                                onClick={() => handleOpenEditSupplier(item)}
                                                                            >
                                                                                <Edit2 size={13} />
                                                                                <span>Edit</span>
                                                                            </button>

                                                                            <button
                                                                                className="sup-topbar-action-btn delete"
                                                                                title="Delete Supplier"
                                                                                onClick={() => setPendingDelete(item)}
                                                                            >
                                                                                <Trash2 size={13} />
                                                                            </button>

                                                                            <button
                                                                                className="sup-topbar-action-btn"
                                                                                title="Collapse"
                                                                                onClick={() => toggleRowExpand(item.id)}
                                                                            >
                                                                                <ChevronUp size={13} />
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    {/* Sub-Tabs Nav */}
                                                                    <div className="sup-tabs-nav">
                                                                        <button
                                                                            className={`sup-tab-btn${activeTab === 'details' ? ' active' : ''}`}
                                                                            onClick={() => setSubTab(item.id, 'details')}
                                                                        >
                                                                            <Building2 size={14} />
                                                                            <span>Supplier Details</span>
                                                                        </button>

                                                                        <button
                                                                            className={`sup-tab-btn${activeTab === 'contacts' ? ' active' : ''}`}
                                                                            onClick={() => setSubTab(item.id, 'contacts')}
                                                                        >
                                                                            <Users size={14} />
                                                                            <span>Contacts</span>
                                                                            <span className="sup-tab-count">{contactsCount}</span>
                                                                        </button>

                                                                        <button
                                                                            className={`sup-tab-btn${activeTab === 'documents' ? ' active' : ''}`}
                                                                            onClick={() => setSubTab(item.id, 'documents')}
                                                                        >
                                                                            <FileText size={14} />
                                                                            <span>Documents</span>
                                                                            <span className="sup-tab-count">{docsCount}</span>
                                                                        </button>

                                                                        <button
                                                                            className={`sup-tab-btn${activeTab === 'components' ? ' active' : ''}`}
                                                                            onClick={() => setSubTab(item.id, 'components')}
                                                                        >
                                                                            <Layers size={14} />
                                                                            <span>Components Sourcing</span>
                                                                            <span className="sup-tab-count">{compCount}</span>
                                                                        </button>
                                                                    </div>

                                                                    {/* Sub-Tab Content */}
                                                                    <div className="sup-tab-content">
                                                                        {/* ── TAB 1: SUPPLIER DETAILS ── */}
                                                                        {activeTab === 'details' && (
                                                                            <div className="sup-details-grid">
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Supplier Code</span>
                                                                                    <span className="sup-detail-value highlight">{item.code}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Supplier Name</span>
                                                                                    <span className="sup-detail-value" style={{ fontWeight: 600 }}>{item.name}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Registration Number</span>
                                                                                    <span className="sup-detail-value">{item.registrationNumber || 'N/A'}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">GST Number</span>
                                                                                    <span className="sup-detail-value">{item.taxNumber || 'N/A'}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Address Line 1</span>
                                                                                    <span className="sup-detail-value">{item.address1 || 'N/A'}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Address Line 2</span>
                                                                                    <span className="sup-detail-value">{item.address2 || 'N/A'}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">City</span>
                                                                                    <span className="sup-detail-value">{item.city || 'N/A'}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">State / Region</span>
                                                                                    <span className="sup-detail-value">{getStateLabel(item)}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Postal Code</span>
                                                                                    <span className="sup-detail-value">{item.postalCode || 'N/A'}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Country Code / Country</span>
                                                                                    <span className="sup-detail-value">{getCountryLabel(item)}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Email Address</span>
                                                                                    <span className="sup-detail-value">{item.email}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Telephone</span>
                                                                                    <span className="sup-detail-value">{item.phone}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Official Website</span>
                                                                                    <span className="sup-detail-value" style={{ color: '#00bcff' }}>{item.website || 'N/A'}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Payment Terms</span>
                                                                                    <span className="sup-detail-value highlight">{getPaymentTermsLabel(item)}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Warranty Terms</span>
                                                                                    <span className="sup-detail-value">{getWarrantyTermsLabel(item)}</span>
                                                                                </div>
                                                                                <div className="sup-detail-field">
                                                                                    <span className="sup-detail-label">Supplier Status</span>
                                                                                    <span className="sup-detail-value">
                                                                                        <span className={`sup-status-badge ${item.status ? 'active' : 'inactive'}`}>
                                                                                            <span className="sup-status-dot" />
                                                                                            {item.status ? (item.supplierStatus || 'Active') : 'Inactive'}
                                                                                        </span>
                                                                                    </span>
                                                                                </div>
                                                                                {item.created_utc && (
                                                                                    <div className="sup-detail-field">
                                                                                        <span className="sup-detail-label">Created UTC / By</span>
                                                                                        <span className="sup-detail-value" style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                                                            {item.created_utc} {item.created_by ? `(${item.created_by})` : ''}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                                {item.last_update_date && (
                                                                                    <div className="sup-detail-field">
                                                                                        <span className="sup-detail-label">Last Updated</span>
                                                                                        <span className="sup-detail-value" style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                                                            {item.last_update_date} {item.last_updated_by ? `(${item.last_updated_by})` : ''}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {/* ── TAB 2: CONTACTS (API: /api/supplier-contacts/) ── */}
                                                                        {activeTab === 'contacts' && (
                                                                            <div>
                                                                                <div className="sup-subtable-wrap">
                                                                                    <div className="sup-subtable-header">
                                                                                        <h4 className="sup-subtable-title">
                                                                                            <Users size={14} color="#00bcff" />
                                                                                            Contacts ({contactsCount})
                                                                                        </h4>
                                                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                                                            <button
                                                                                                className="sup-topbar-action-btn"
                                                                                                onClick={() => fetchSupplierContacts(item.id)}
                                                                                                title="Refresh Contacts"
                                                                                            >
                                                                                                <RefreshCw size={12} className={subTabLoading.contacts ? 'spin-icon' : ''} />
                                                                                            </button>
                                                                                            <button
                                                                                                className="sup-inline-btn"
                                                                                                onClick={() => handleOpenAddContactModal(item.id)}
                                                                                            >
                                                                                                <Plus size={13} /> Add Contact
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>

                                                                                    <table className="sup-subtable">
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th>Contact Name</th>
                                                                                                <th>Designation / Role</th>
                                                                                                <th>Email</th>
                                                                                                <th>Telephone</th>
                                                                                                <th className="center">Primary</th>
                                                                                                <th className="center">Status</th>
                                                                                                <th className="center">Action</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {subTabLoading.contacts ? (
                                                                                                <tr>
                                                                                                    <td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#00bcff' }}>
                                                                                                        <Loader2 size={16} className="spin-icon" style={{ display: 'inline', marginRight: 6 }} />
                                                                                                        Loading contacts...
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ) : contactsList.length === 0 ? (
                                                                                                <tr>
                                                                                                    <td colSpan={7} style={{ textAlign: 'center', padding: '16px', color: '#64748b' }}>
                                                                                                        No contacts added yet. Click "+ Add Contact" to register a representative.
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ) : (
                                                                                                contactsList.map(c => (
                                                                                                    <tr key={c.id}>
                                                                                                        <td style={{ fontWeight: 600, color: '#f1f5f9' }}>{c.name}</td>
                                                                                                        <td style={{ color: '#cbd5e1' }}>{c.designation}</td>
                                                                                                        <td style={{ color: '#94a3b8' }}>{c.email}</td>
                                                                                                        <td style={{ color: '#cbd5e1' }}>{c.phone}</td>
                                                                                                        <td className="center">
                                                                                                            {c.isPrimary ? (
                                                                                                                <span className="sup-primary-badge">
                                                                                                                    <ShieldCheck size={12} /> Primary
                                                                                                                </span>
                                                                                                            ) : (
                                                                                                                <span className="sup-secondary-badge">—</span>
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td className="center">
                                                                                                            <button
                                                                                                                className={`comp-toggle-btn ${c.status ? 'on' : ''}`}
                                                                                                                onClick={() => handleToggleContactStatus(item.id, c.id, c.status)}
                                                                                                                title={c.status ? 'Active' : 'Inactive'}
                                                                                                            >
                                                                                                                <span className="comp-toggle-thumb" />
                                                                                                            </button>
                                                                                                        </td>
                                                                                                        <td className="center">
                                                                                                            <span className="assets-action-group">
                                                                                                                <button
                                                                                                                    className="assets-action-btn edit"
                                                                                                                    title="Edit Contact"
                                                                                                                    onClick={() => handleOpenEditContactModal(item.id, c)}
                                                                                                                >
                                                                                                                    <Edit2 size={13} />
                                                                                                                </button>
                                                                                                                <button
                                                                                                                    className="assets-action-btn delete"
                                                                                                                    title="Remove Contact"
                                                                                                                    onClick={() => handleDeleteContact(item.id, c.id)}
                                                                                                                >
                                                                                                                    <Trash2 size={13} />
                                                                                                                </button>
                                                                                                            </span>
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                ))
                                                                                            )}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {/* ── TAB 3: DOCUMENTS (API: /api/supplier-documents/) ── */}
                                                                        {activeTab === 'documents' && (
                                                                            <div>
                                                                                <div className="sup-subtable-wrap">
                                                                                    <div className="sup-subtable-header">
                                                                                        <h4 className="sup-subtable-title">
                                                                                            <FileText size={14} color="#00bcff" />
                                                                                            Documents ({docsCount})
                                                                                        </h4>
                                                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                                                            <button
                                                                                                className="sup-topbar-action-btn"
                                                                                                onClick={() => fetchSupplierDocuments(item.id)}
                                                                                                title="Refresh Documents"
                                                                                            >
                                                                                                <RefreshCw size={12} className={subTabLoading.documents ? 'spin-icon' : ''} />
                                                                                            </button>
                                                                                            <button
                                                                                                className="sup-inline-btn"
                                                                                                onClick={() => handleOpenAddDocModal(item.id)}
                                                                                            >
                                                                                                <Plus size={13} /> Add Document
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>

                                                                                    <div style={{ overflowX: 'auto' }}>
                                                                                        <table className="sup-subtable">
                                                                                            <thead>
                                                                                                <tr>
                                                                                                    <th>Document Type</th>
                                                                                                    <th>Document Name</th>
                                                                                                    <th>Original Filename</th>
                                                                                                    <th>Effective Date</th>
                                                                                                    <th>Expiry Date</th>
                                                                                                    <th>Uploaded UTC</th>
                                                                                                    <th className="center">Status</th>
                                                                                                    <th className="center">Action</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {subTabLoading.documents ? (
                                                                                                    <tr>
                                                                                                        <td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#00bcff' }}>
                                                                                                            <Loader2 size={16} className="spin-icon" style={{ display: 'inline', marginRight: 6 }} />
                                                                                                            Loading documents...
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                ) : docsList.length === 0 ? (
                                                                                                    <tr>
                                                                                                        <td colSpan={8} style={{ textAlign: 'center', padding: '18px', color: '#64748b' }}>
                                                                                                            No documents filed yet. Click "+ Add Document" to upload compliance files.
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                ) : (
                                                                                                    docsList.map(d => (
                                                                                                        <tr key={d.id}>
                                                                                                            <td style={{ color: '#cbd5e1', fontWeight: 500 }}>{getDocumentTypeName(d)}</td>
                                                                                                            <td style={{ fontWeight: 600, color: '#f1f5f9' }}>{d.document_name || d.name}</td>
                                                                                                            <td>
                                                                                                                <span
                                                                                                                    className="sup-filename-pill"
                                                                                                                    title={`Download ${d.original_filename || d.originalFilename || 'document'}`}
                                                                                                                    onClick={() => handleDownloadDocument(d)}
                                                                                                                    style={{ cursor: 'pointer' }}
                                                                                                                >
                                                                                                                    <FileText size={12} color="#00bcff" />
                                                                                                                    <span>{getDisplayFilename(d.original_filename || d.originalFilename)}</span>
                                                                                                                </span>
                                                                                                            </td>
                                                                                                            <td style={{ color: '#94a3b8' }}>{formatDisplayDate(d.effective_date || d.effectiveDate)}</td>
                                                                                                            <td style={{ color: '#cbd5e1' }}>{formatDisplayDate(d.expiry_date || d.expiryDate)}</td>
                                                                                                            <td>
                                                                                                                <span className="sup-utc-tag">{formatUtcDisplay(d.uploaded_utc || d.uploadedUtc)}</span>
                                                                                                            </td>
                                                                                                            <td className="center">
                                                                                                                <button
                                                                                                                    className={`comp-toggle-btn ${d.status ? 'on' : ''}`}
                                                                                                                    onClick={() => handleToggleDocStatus(item.id, d.id, d.status)}
                                                                                                                    title={d.status ? 'Active' : 'Inactive'}
                                                                                                                >
                                                                                                                    <span className="comp-toggle-thumb" />
                                                                                                                </button>
                                                                                                            </td>
                                                                                                            <td className="center">
                                                                                                                <span className="assets-action-group">
                                                                                                                    <button
                                                                                                                        className="assets-action-btn edit"
                                                                                                                        title="View Document Details & Preview"
                                                                                                                        onClick={() => handleViewDocument(item, d)}
                                                                                                                    >
                                                                                                                        <Eye size={13} />
                                                                                                                    </button>
                                                                                                                    <button
                                                                                                                        className="assets-action-btn edit"
                                                                                                                        title="Download Document File"
                                                                                                                        onClick={() => handleDownloadDocument(d)}
                                                                                                                        disabled={downloadingDocId === (d.supplier_document_id ?? d.id)}
                                                                                                                        style={downloadingDocId === (d.supplier_document_id ?? d.id) ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                                                                                                    >
                                                                                                                        {downloadingDocId === (d.supplier_document_id ?? d.id) ? (
                                                                                                                            <RefreshCw size={13} className="spin-icon" />
                                                                                                                        ) : (
                                                                                                                            <Download size={13} />
                                                                                                                        )}
                                                                                                                    </button>
                                                                                                                    <button
                                                                                                                        className="assets-action-btn edit"
                                                                                                                        title="Edit Document"
                                                                                                                        onClick={() => handleOpenEditDocModal(item.id, d)}
                                                                                                                    >
                                                                                                                        <Edit2 size={13} />
                                                                                                                    </button>
                                                                                                                    <button
                                                                                                                        className="assets-action-btn delete"
                                                                                                                        title="Delete Document"
                                                                                                                        onClick={() => handleDeleteDocument(item.id, d.id)}
                                                                                                                    >
                                                                                                                        <Trash2 size={13} />
                                                                                                                    </button>
                                                                                                                </span>
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    ))
                                                                                                )}
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {/* ── TAB 4: COMPONENTS (API: /api/supplier-components/) ── */}
                                                                        {activeTab === 'components' && (
                                                                            <div>
                                                                                <div className="sup-subtable-wrap">
                                                                                    <div className="sup-subtable-header">
                                                                                        <h4 className="sup-subtable-title">
                                                                                            <Layers size={14} color="#00bcff" />
                                                                                            Components Sourced ({compCount})
                                                                                        </h4>
                                                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                                                            <button
                                                                                                className="sup-topbar-action-btn"
                                                                                                onClick={() => fetchSupplierComponents(item.id)}
                                                                                                title="Refresh Components"
                                                                                            >
                                                                                                <RefreshCw size={12} className={subTabLoading.components ? 'spin-icon' : ''} />
                                                                                            </button>
                                                                                            <button
                                                                                                className="sup-inline-btn"
                                                                                                onClick={() => handleOpenAddComponent(item.id)}
                                                                                            >
                                                                                                <Plus size={13} /> Add Component
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>

                                                                                    <table className="sup-subtable">
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th>ID</th>
                                                                                                <th>Component Code</th>
                                                                                                <th>Component Name</th>
                                                                                                <th>Part Number</th>
                                                                                                <th className="center">Price</th>
                                                                                                <th className="center">Tax (%)</th>
                                                                                                <th className="center">Total</th>
                                                                                                <th className="center">MOQ</th>
                                                                                                <th className="center">Lead Time</th>
                                                                                                <th className="center">Preferred</th>
                                                                                                <th className="center">Status</th>
                                                                                                <th className="center">Action</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {subTabLoading.components ? (
                                                                                                <tr>
                                                                                                    <td colSpan={12} style={{ textAlign: 'center', padding: '20px', color: '#00bcff' }}>
                                                                                                        <Loader2 size={16} className="spin-icon" style={{ display: 'inline', marginRight: 6 }} />
                                                                                                        Loading mapped components...
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ) : componentsList.length === 0 ? (
                                                                                                <tr>
                                                                                                    <td colSpan={12} style={{ textAlign: 'center', padding: '16px', color: '#64748b' }}>
                                                                                                        No components mapped to this supplier yet. Click "+ Add Component" to map one.
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ) : (
                                                                                                componentsList.map((c, cIdx) => (
                                                                                                    <tr key={c.id || cIdx}>
                                                                                                        <td style={{ color: '#64748b' }}>{c.id || cIdx + 1}</td>
                                                                                                        <td style={{ color: '#00bcff', fontFamily: 'monospace' }}>{c.code}</td>
                                                                                                        <td style={{ fontWeight: 600, color: '#f1f5f9' }}>{c.name}</td>
                                                                                                        <td style={{ color: '#cbd5e1', fontFamily: 'monospace' }}>{c.supplier_part_number || '—'}</td>
                                                                                                        <td className="center" style={{ fontWeight: 600, color: '#38caff' }}>
                                                                                                            {c.currency_code} {formatTwoDecimals(c.price)}
                                                                                                        </td>
                                                                                                        <td className="center" style={{ color: '#94a3b8' }}>{formatTwoDecimals(c.tax)}%</td>
                                                                                                        <td className="center" style={{ fontWeight: 700, color: '#4ade80' }}>
                                                                                                            {c.currency_code} {formatTwoDecimals(c.total)}
                                                                                                        </td>
                                                                                                        <td className="center" style={{ color: '#cbd5e1' }}>{formatTwoDecimals(c.minimum_order_quantity)}</td>
                                                                                                        <td className="center" style={{ color: '#cbd5e1' }}>{c.lead_time_days} Days</td>
                                                                                                        <td className="center">
                                                                                                            {c.is_preferred ? (
                                                                                                                <span className="sup-preferred-badge">
                                                                                                                    <Star size={11} fill="#fbbf24" /> Yes
                                                                                                                </span>
                                                                                                            ) : (
                                                                                                                <span className="sup-secondary-badge">No</span>
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td className="center">
                                                                                                            <button
                                                                                                                className={`comp-toggle-btn ${c.status ? 'on' : ''}`}
                                                                                                                onClick={() => handleToggleCompStatus(item.id, c.id, c.status)}
                                                                                                                title={c.status ? 'Active' : 'Inactive'}
                                                                                                            >
                                                                                                                <span className="comp-toggle-thumb" />
                                                                                                            </button>
                                                                                                        </td>
                                                                                                        <td className="center">
                                                                                                            <span className="assets-action-group">
                                                                                                                <button
                                                                                                                    className="assets-action-btn edit"
                                                                                                                    title="Edit Sourcing Details"
                                                                                                                    onClick={() => handleOpenEditComponent(item.id, c)}
                                                                                                                >
                                                                                                                    <Edit2 size={13} />
                                                                                                                </button>
                                                                                                                <button
                                                                                                                    className="assets-action-btn delete"
                                                                                                                    title="Unlink Component"
                                                                                                                    onClick={() => handleDeleteComponentMapping(item.id, c.id)}
                                                                                                                >
                                                                                                                    <Trash2 size={13} />
                                                                                                                </button>
                                                                                                            </span>
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                ))
                                                                                            )}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer / Pagination */}
                        <div className="comp-footer">
                            <div className="comp-showing-text">
                                Showing {sorted.length > 0 ? (currentPage - 1) * ROWS_PER_PAGE + 1 : 0} to{' '}
                                {Math.min(currentPage * ROWS_PER_PAGE, sorted.length)} of {sorted.length} suppliers
                            </div>

                            <div className="comp-pagination">
                                <button
                                    className="comp-page-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                >
                                    &lt;
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        className={`comp-page-btn${currentPage === page ? ' active' : ''}`}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    className="comp-page-btn"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                >
                                    &gt;
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── ADD / EDIT SUPPLIER MODAL (API: /api/suppliers/create/ or /update/) ── */}
            <AnimatePresence>
                {showSupplierModal && (
                    <div className="sup-modal-overlay" onClick={() => !isSubmitting && setShowSupplierModal(false)}>
                        <motion.div
                            className="sup-modal-box"
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            onClick={e => e.stopPropagation()}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '88vh',
                                maxHeight: '840px',
                                overflow: 'hidden',
                            }}
                        >
                            <div className="sup-modal-header" style={{ flexShrink: 0 }}>
                                <div>
                                    <h2 className="sup-modal-title">
                                        {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
                                    </h2>
                                    <p className="sup-modal-subtitle">
                                        {editingSupplier
                                            ? 'Update supplier sourcing partner profile and commercial terms.'
                                            : 'Register a new approved vendor for hardware components and BOM sourcing.'}
                                    </p>
                                </div>
                                <button
                                    className="sup-modal-close"
                                    onClick={() => !isSubmitting && setShowSupplierModal(false)}
                                    title="Close"
                                    disabled={isSubmitting}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form
                                onSubmit={handleSaveSupplier}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: '1 1 auto',
                                    minHeight: 0,
                                    height: 'calc(100% - 68px)',
                                    overflow: 'hidden',
                                    margin: 0,
                                }}
                            >
                                <div
                                    className="sup-modal-body"
                                    style={{
                                        flex: '1 1 auto',
                                        overflowY: 'auto',
                                        minHeight: 0,
                                        padding: '22px 24px',
                                    }}
                                >
                                    {formErrors.submit && (
                                        <div
                                            style={{
                                                background: 'rgba(239, 68, 68, 0.12)',
                                                border: '1px solid rgba(239, 68, 68, 0.35)',
                                                color: '#f87171',
                                                padding: '10px 14px',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                marginBottom: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            <AlertCircle size={16} style={{ flexShrink: 0 }} />
                                            <span>{formErrors.submit}</span>
                                        </div>
                                    )}

                                    <div className="sup-form-grid">
                                        {/* Supplier Code */}
                                        <div className="comp-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '18px' }}>
                                                <label className="comp-form-label" style={{ marginBottom: 0 }}>Supplier Code *</label>
                                                {!editingSupplier && (
                                                    <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic', lineHeight: 1 }}>Auto-suggested</span>
                                                )}
                                            </div>
                                            <input
                                                className={`comp-form-input${formErrors.code ? ' error' : ''}`}
                                                name="code"
                                                placeholder="e.g. SUP-0001"
                                                value={supplierForm.code}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.code && <p className="site-form-error">{formErrors.code}</p>}
                                        </div>
                                        {/* Tax / GST Number */}
                                        <div className="comp-form-group">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '18px' }}>
                                                <label className="comp-form-label" style={{ marginBottom: 0 }}>GST Number</label>
                                                {isVerifyingGst && (
                                                    <span style={{ fontSize: '11px', color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 500, lineHeight: 1 }}>
                                                        <Loader2 size={12} className="spin-icon" /> Verifying GST...
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    className={`comp-form-input${formErrors.taxNumber ? ' error' : ''}`}
                                                    name="taxNumber"
                                                    placeholder="e.g. 06AAFCV5179D2Z7"
                                                    value={supplierForm.taxNumber}
                                                    onChange={handleSupplierFormChange}
                                                    onBlur={() => {
                                                        const clean = String(supplierForm.taxNumber || '').trim().toUpperCase();
                                                        if (!clean) {
                                                            if (formErrors.taxNumber) setFormErrors(prev => ({ ...prev, taxNumber: '' }));
                                                            return;
                                                        }
                                                        if (clean.length === 15) {
                                                            if (clean !== lastVerifiedGstRef.current) {
                                                                verifyGstDetails(clean);
                                                            }
                                                        } else if (clean.length > 0 && clean.length < 15 && (/^\d/.test(clean) || !supplierForm.countryCode || supplierForm.countryCode.toUpperCase() === 'IN')) {
                                                            setFormErrors(prev => ({ ...prev, taxNumber: 'GST number must be 15 characters (e.g. 06AAFCV5179D2Z7).' }));
                                                            clearAutoFilledGstFields();
                                                        }
                                                    }}
                                                    style={{ textTransform: 'uppercase', paddingRight: isVerifyingGst ? '32px' : undefined }}
                                                />
                                                {isVerifyingGst && (
                                                    <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                                                        <Loader2 size={15} className="spin-icon" style={{ color: '#2563eb' }} />
                                                    </div>
                                                )}
                                            </div>
                                            {formErrors.taxNumber && <p className="site-form-error">{formErrors.taxNumber}</p>}
                                        </div>

                                        {/* Supplier Name */}
                                        <div className="comp-form-group" style={{ gridColumn: '1 / -1' }}>
                                            <label className="comp-form-label">Supplier Name *</label>
                                            <input
                                                className={`comp-form-input${formErrors.name ? ' error' : ''}`}
                                                name="name"
                                                placeholder="e.g. ABC India Pvt Ltd"
                                                value={supplierForm.name}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.name && <p className="site-form-error">{formErrors.name}</p>}
                                        </div>


                                        {/* Registration Number */}
                                        <div className="comp-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '18px' }}>
                                                <label className="comp-form-label" style={{ marginBottom: 0 }}>Registration Number</label>
                                            </div>
                                            <input
                                                className={`comp-form-input${formErrors.registrationNumber ? ' error' : ''}`}
                                                name="registrationNumber"
                                                placeholder="e.g. REG-001"
                                                value={supplierForm.registrationNumber}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.registrationNumber && <p className="site-form-error">{formErrors.registrationNumber}</p>}
                                        </div>

                                        {/* Status (Auto-filled from GST, Read-only) */}
                                        <div className="comp-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '18px' }}>
                                                <label className="comp-form-label" style={{ marginBottom: 0 }}>GST Status</label>
                                                <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic', lineHeight: 1 }}>Auto from GST</span>
                                            </div>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    className="comp-form-input"
                                                    name="gstStatus"
                                                    placeholder=""
                                                    value={supplierForm.gstStatus || ''}
                                                    readOnly
                                                    disabled
                                                    style={{
                                                        cursor: 'not-allowed',
                                                        background: 'rgba(15, 23, 42, 0.65)',
                                                        color: supplierForm.gstStatus
                                                            ? (String(supplierForm.gstStatus).toLowerCase() === 'active' ? '#4ade80' : '#f87171')
                                                            : '#64748b',
                                                        fontWeight: supplierForm.gstStatus ? 600 : 400,
                                                        borderColor: 'rgba(255, 255, 255, 0.08)'
                                                    }}
                                                />
                                                {supplierForm.gstStatus && (
                                                    <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span
                                                            style={{
                                                                display: 'inline-block',
                                                                width: '8px',
                                                                height: '8px',
                                                                borderRadius: '50%',
                                                                backgroundColor: String(supplierForm.gstStatus).toLowerCase() === 'active' ? '#22c55e' : '#ef4444'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">Email Address *</label>
                                            <input
                                                className={`comp-form-input${formErrors.email ? ' error' : ''}`}
                                                type="email"
                                                name="email"
                                                placeholder="e.g. contact@abc.com"
                                                value={supplierForm.email}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.email && <p className="site-form-error">{formErrors.email}</p>}
                                        </div>

                                        {/* Telephone */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">Telephone *</label>
                                            <input
                                                className={`comp-form-input${formErrors.phone ? ' error' : ''}`}
                                                type="text"
                                                name="phone"
                                                placeholder="e.g. 98765 43210"
                                                value={supplierForm.phone}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.phone && <p className="site-form-error">{formErrors.phone}</p>}
                                        </div>

                                        {/* Address Line 1 */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">Address Line 1 *</label>
                                            <input
                                                className={`comp-form-input${formErrors.address1 ? ' error' : ''}`}
                                                name="address1"
                                                placeholder="e.g. 123 Industrial Area"
                                                value={supplierForm.address1}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.address1 && <p className="site-form-error">{formErrors.address1}</p>}
                                        </div>

                                        {/* Address Line 2 */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">Address Line 2</label>
                                            <input
                                                className={`comp-form-input${formErrors.address2 ? ' error' : ''}`}
                                                name="address2"
                                                placeholder="e.g. Building A"
                                                value={supplierForm.address2}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.address2 && <p className="site-form-error">{formErrors.address2}</p>}
                                        </div>



                                        {/* Country Code / Country */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">Country Code / Country *</label>
                                            <div className="comp-form-select-wrap">
                                                <select
                                                    className={`comp-form-select${formErrors.country_id ? ' error' : ''}`}
                                                    name="country_id"
                                                    value={supplierForm.country_id}
                                                    onChange={handleSupplierFormChange}
                                                    disabled={loadingCountries}
                                                >
                                                    <option value="">{loadingCountries ? 'Loading countries...' : 'Select Country'}</option>
                                                    {countries.map(c => (
                                                        <option key={c.country_id} value={c.country_id}>
                                                            {c.country_name} ({c.country_code})
                                                        </option>
                                                    ))}
                                                </select>
                                                <ChevronDown size={14} className="comp-form-select-icon" />
                                            </div>
                                            {formErrors.country_id && <p className="site-form-error">{formErrors.country_id}</p>}
                                        </div>

                                        {/* State / Region */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">State / Region *</label>
                                            <div className="comp-form-select-wrap">
                                                <select
                                                    className={`comp-form-select${formErrors.state_id ? ' error' : ''}`}
                                                    name="state_id"
                                                    value={supplierForm.state_id}
                                                    onChange={handleSupplierFormChange}
                                                    disabled={loadingStates || !supplierForm.country_id}
                                                >
                                                    {!supplierForm.country_id ? (
                                                        <option value="">Select Country first</option>
                                                    ) : loadingStates ? (
                                                        <option value="">Loading states...</option>
                                                    ) : (
                                                        <>
                                                            <option value="">Select State / Region</option>
                                                            {states.map(s => (
                                                                <option key={s.state_id} value={s.state_id}>
                                                                    {s.state_name}
                                                                </option>
                                                            ))}
                                                        </>
                                                    )}
                                                </select>
                                                <ChevronDown size={14} className="comp-form-select-icon" />
                                            </div>
                                            {formErrors.state_id && <p className="site-form-error">{formErrors.state_id}</p>}
                                        </div>

                                        {/* City / District */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">City / District *</label>
                                            <input
                                                className={`comp-form-input${formErrors.city ? ' error' : ''}`}
                                                name="city"
                                                placeholder="e.g. Hisar / Hyderabad"
                                                value={supplierForm.city}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.city && <p className="site-form-error">{formErrors.city}</p>}
                                        </div>

                                        {/* Postal Code */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">Postal Code</label>
                                            <input
                                                className={`comp-form-input${formErrors.postalCode ? ' error' : ''}`}
                                                name="postalCode"
                                                placeholder="e.g. 500001"
                                                value={supplierForm.postalCode}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.postalCode && <p className="site-form-error">{formErrors.postalCode}</p>}
                                        </div>

                                        {/* Website */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">Website</label>
                                            <input
                                                className={`comp-form-input${formErrors.website ? ' error' : ''}`}
                                                name="website"
                                                placeholder="e.g. https://abc.com"
                                                value={supplierForm.website}
                                                onChange={handleSupplierFormChange}
                                            />
                                            {formErrors.website && <p className="site-form-error">{formErrors.website}</p>}
                                        </div>

                                        {/* Warranty Terms */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">Warranty Terms *</label>
                                            <div className="comp-form-select-wrap">
                                                <select
                                                    className={`comp-form-select${formErrors.warranty_terms ? ' error' : ''}`}
                                                    name="warranty_terms"
                                                    value={supplierForm.warranty_terms}
                                                    onChange={handleSupplierFormChange}
                                                    disabled={loadingWarrantyTerms}
                                                >
                                                    <option value="">{loadingWarrantyTerms ? 'Loading warranty terms...' : 'Select Warranty Terms'}</option>
                                                    {warrantyTermsList.map(w => (
                                                        <option key={w.warranty_terms_id} value={w.warranty_terms_id}>
                                                            {w.warranty_terms}
                                                        </option>
                                                    ))}
                                                </select>
                                                <ChevronDown size={14} className="comp-form-select-icon" />
                                            </div>
                                            {formErrors.warranty_terms && <p className="site-form-error">{formErrors.warranty_terms}</p>}
                                        </div>

                                        {/* Supplier Status */}
                                        <div className="comp-form-group">
                                            <label className="comp-form-label">Supplier Status (Review) *</label>
                                            <div className="comp-form-select-wrap">
                                                <select
                                                    className={`comp-form-select${formErrors.supplierStatus ? ' error' : ''}`}
                                                    name="supplierStatus"
                                                    value={supplierForm.supplierStatus}
                                                    onChange={handleSupplierFormChange}
                                                >
                                                    <option value="">Select Supplier Status</option>
                                                    <option value="Approved">Approved</option>
                                                    <option value="Pending">Pending Review</option>
                                                    <option value="Under Evaluation">Under Evaluation</option>
                                                    <option value="Preferred">Preferred Partner</option>
                                                </select>
                                                <ChevronDown size={14} className="comp-form-select-icon" />
                                            </div>
                                            {formErrors.supplierStatus && <p className="site-form-error">{formErrors.supplierStatus}</p>}
                                        </div>



                                    </div>
                                </div>

                                <div
                                    className="sup-modal-footer"
                                    style={{
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        gap: '14px',
                                        padding: '16px 24px',
                                        borderTop: '1px solid rgba(8, 120, 255, 0.25)',
                                        background: '#020813',
                                        position: 'sticky',
                                        bottom: 0,
                                        zIndex: 20,
                                    }}
                                >
                                    <button
                                        type="button"
                                        className="comp-btn-cancel"
                                        onClick={() => setShowSupplierModal(false)}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="comp-btn-save" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                <Loader2 size={14} className="spin-icon" />
                                                Saving...
                                            </span>
                                        ) : (
                                            editingSupplier ? 'Save Supplier' : 'Add Supplier'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ── ADD / EDIT CONTACT MODAL (API: /api/supplier-contacts/create/ or /update/) ── */}
            <AnimatePresence>
                {contactModalSupplierId && (
                    <div className="sup-modal-overlay" onClick={() => !isSubmitting && setContactModalSupplierId(null)}>
                        <motion.div
                            className="sup-comp-modal-box"
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.94 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="sup-modal-header" style={{ padding: '0 0 16px 0' }}>
                                <div>
                                    <h3 className="sup-modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <UserCheck size={18} color="#00bcff" />
                                        {editingContactId ? 'Edit Contact Person' : 'Add New Contact Person'}
                                    </h3>
                                    <p className="sup-modal-subtitle">
                                        Manage authorized representative details. (Note: Only one Primary contact allowed per supplier)
                                    </p>
                                </div>
                                <button
                                    className="sup-modal-close"
                                    onClick={() => !isSubmitting && setContactModalSupplierId(null)}
                                    disabled={isSubmitting}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleSaveContactModal} noValidate style={{ marginTop: '16px' }}>
                                {contactFormErrors.supplier && (
                                    <p
                                        className="site-form-error"
                                        style={{
                                            color: '#f87171',
                                            fontSize: '12px',
                                            marginBottom: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            background: 'rgba(239, 68, 68, 0.1)',
                                            padding: '8px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(239, 68, 68, 0.25)',
                                        }}
                                    >
                                        <AlertCircle size={14} style={{ flexShrink: 0 }} />
                                        <span>{contactFormErrors.supplier}</span>
                                    </p>
                                )}

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    {/* Contact Name * */}
                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Contact Name *</label>
                                        <input
                                            className={`comp-form-input${contactFormErrors.name ? ' error' : ''}`}
                                            placeholder="e.g. John Smith"
                                            value={contactFormData.name}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setContactFormData(prev => ({ ...prev, name: val }));
                                                if (contactFormErrors.name) setContactFormErrors(prev => ({ ...prev, name: '' }));
                                            }}
                                            autoFocus
                                        />
                                        {contactFormErrors.name && <p className="site-form-error">{contactFormErrors.name}</p>}
                                    </div>

                                    {/* Designation / Role */}
                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Designation / Role</label>
                                        <input
                                            className={`comp-form-input${contactFormErrors.designation ? ' error' : ''}`}
                                            placeholder="e.g. Sales Manager"
                                            value={contactFormData.designation}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setContactFormData(prev => ({ ...prev, designation: val }));
                                                if (contactFormErrors.designation) setContactFormErrors(prev => ({ ...prev, designation: '' }));
                                            }}
                                        />
                                        {contactFormErrors.designation && <p className="site-form-error">{contactFormErrors.designation}</p>}
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    {/* Email Address * */}
                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Email Address *</label>
                                        <input
                                            className={`comp-form-input${contactFormErrors.email ? ' error' : ''}`}
                                            type="text"
                                            placeholder="e.g. john.smith@example.com"
                                            value={contactFormData.email}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setContactFormData(prev => ({ ...prev, email: val }));
                                                if (contactFormErrors.email) setContactFormErrors(prev => ({ ...prev, email: '' }));
                                            }}
                                        />
                                        {contactFormErrors.email && <p className="site-form-error">{contactFormErrors.email}</p>}
                                    </div>

                                    {/* Telephone * */}
                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Telephone *</label>
                                        <input
                                            className={`comp-form-input${contactFormErrors.phone ? ' error' : ''}`}
                                            type="text"
                                            placeholder="e.g. 98765 43210"
                                            value={contactFormData.phone}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setContactFormData(prev => ({ ...prev, phone: val }));
                                                if (contactFormErrors.phone) setContactFormErrors(prev => ({ ...prev, phone: '' }));
                                            }}
                                        />
                                        {contactFormErrors.phone && <p className="site-form-error">{contactFormErrors.phone}</p>}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.06)', marginTop: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#cbd5e1', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={contactFormData.isPrimary}
                                            onChange={e => setContactFormData(prev => ({ ...prev, isPrimary: e.target.checked }))}
                                        />
                                        <span>Set as Primary Contact</span>
                                    </label>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>Status * :</span>
                                        <button
                                            type="button"
                                            className={`comp-toggle-btn ${contactFormData.status ? 'on' : ''}`}
                                            onClick={() => {
                                                setContactFormData(prev => ({ ...prev, status: !prev.status }));
                                                if (contactFormErrors.status) setContactFormErrors(prev => ({ ...prev, status: '' }));
                                            }}
                                        >
                                            <span className="comp-toggle-thumb" />
                                        </button>
                                        <span style={{ fontSize: '12px', color: contactFormData.status ? '#00bcff' : '#64748b' }}>
                                            {contactFormData.status ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                                {contactFormErrors.status && <p className="site-form-error" style={{ marginTop: '6px' }}>{contactFormErrors.status}</p>}

                                <div className="comp-form-actions" style={{ marginTop: '20px' }}>
                                    <button
                                        type="button"
                                        className="comp-btn-cancel"
                                        onClick={() => setContactModalSupplierId(null)}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="comp-btn-save" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                <Loader2 size={14} className="spin-icon" />
                                                Saving...
                                            </span>
                                        ) : (
                                            editingContactId ? 'Update Contact' : 'Save Contact'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ── ADD / UPLOAD / EDIT DOCUMENT MODAL (API: /api/supplier-documents/) ── */}
            <AnimatePresence>
                {docModalSupplierId && (
                    <div className="sup-modal-overlay" onClick={handleCloseDocModal}>
                        <motion.div
                            className="sup-comp-modal-box"
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.94 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="sup-modal-header" style={{ padding: '0 0 16px 0' }}>
                                <div>
                                    <h3 className="sup-modal-title">
                                        {editingDocId ? 'Edit Supplier Document' : 'Upload / File New Document'}
                                    </h3>
                                    <p className="sup-modal-subtitle">
                                        Attach compliance certificates, contracts, and technical test reports.
                                    </p>
                                </div>
                                <button
                                    className="sup-modal-close"
                                    onClick={handleCloseDocModal}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleSaveDocModal} noValidate style={{ marginTop: '16px' }}>
                                {docFormErrors.submit && (
                                    <div
                                        style={{
                                            background: 'rgba(239, 68, 68, 0.12)',
                                            border: '1px solid rgba(239, 68, 68, 0.35)',
                                            color: '#f87171',
                                            padding: '10px 14px',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            marginBottom: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        <AlertCircle size={16} style={{ flexShrink: 0 }} />
                                        <span>{docFormErrors.submit}</span>
                                    </div>
                                )}

                                {/* Supplier Dropdown */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label" htmlFor="doc_supplier_id">Supplier *</label>
                                    <div className="comp-form-select-wrap">
                                        <select
                                            id="doc_supplier_id"
                                            name="supplier_id"
                                            className={`comp-form-select${docFormErrors.supplier_id ? ' error' : ''}`}
                                            value={docFormData.supplier_id || docModalSupplierId || ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setDocFormData(prev => ({ ...prev, supplier_id: val }));
                                                setDocModalSupplierId(val ? Number(val) : null);
                                                if (docFormErrors.supplier_id || docFormErrors.submit) {
                                                    setDocFormErrors(prev => {
                                                        const next = { ...prev };
                                                        delete next.supplier_id;
                                                        delete next.submit;
                                                        return next;
                                                    });
                                                }
                                            }}
                                            disabled={isDocSubmitting || Boolean(editingDocId)}
                                        >
                                            <option value="">-- Select Active Supplier --</option>
                                            {activeSuppliers.map(s => (
                                                <option key={s.id} value={s.id}>
                                                    {s.code} - {s.name}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={14} className="comp-form-select-icon" />
                                    </div>
                                    {docFormErrors.supplier_id && (
                                        <p className="site-form-error">{docFormErrors.supplier_id}</p>
                                    )}
                                </div>

                                {/* Document Type */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label" htmlFor="document_type_id">Document Type *</label>
                                    <div className="comp-form-select-wrap">
                                        <select
                                            id="document_type_id"
                                            name="document_type_id"
                                            className={`comp-form-select${docFormErrors.document_type_id ? ' error' : ''}`}
                                            value={docFormData.document_type_id}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setDocFormData(prev => ({ ...prev, document_type_id: val }));
                                                if (docFormErrors.document_type_id || docFormErrors.submit) {
                                                    setDocFormErrors(prev => {
                                                        const next = { ...prev };
                                                        delete next.document_type_id;
                                                        delete next.submit;
                                                        return next;
                                                    });
                                                }
                                            }}
                                        >
                                            <option value="">
                                                {loadingDocTypes ? 'Loading document types...' : '-- Select Document Type --'}
                                            </option>
                                            {activeDocTypes.map(dt => (
                                                <option key={dt.id} value={dt.id}>
                                                    {dt.name || `Type #${dt.id}`}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={14} className="comp-form-select-icon" />
                                    </div>
                                    {docFormErrors.document_type_id && (
                                        <p className="site-form-error">{docFormErrors.document_type_id}</p>
                                    )}
                                </div>

                                {/* Document Name */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Document Name *</label>
                                    <input
                                        className={`comp-form-input${docFormErrors.document_name ? ' error' : ''}`}
                                        placeholder="e.g. Supplier Tax Certificate"
                                        maxLength={2000}
                                        value={docFormData.document_name}
                                        onChange={e => {
                                            const val = e.target.value;
                                            setDocFormData(prev => ({ ...prev, document_name: val }));
                                            if (docFormErrors.document_name || docFormErrors.submit) {
                                                setDocFormErrors(prev => {
                                                    const next = { ...prev };
                                                    delete next.document_name;
                                                    delete next.submit;
                                                    return next;
                                                });
                                            }
                                        }}
                                    />
                                    {docFormErrors.document_name && (
                                        <p className="site-form-error">{docFormErrors.document_name}</p>
                                    )}
                                </div>

                                {/* Original Filename / Choose File */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">
                                        Original Filename *
                                    </label>
                                    <div className="sup-file-chooser-wrap">
                                        <label className="sup-file-btn">
                                            <Upload size={13} />
                                            <span>Choose File</span>
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                style={{ display: 'none' }}
                                                onChange={handleDocFileChange}
                                            />
                                        </label>
                                        <span
                                            className="sup-file-chosen-name"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                borderColor: (docFormErrors.original_filename || docFileError) ? '#ef4444' : undefined,
                                            }}
                                        >
                                            {selectedDocFile
                                                ? selectedDocFile.name
                                                : (docFormData.existingFilename
                                                    ? `${getDisplayFilename(docFormData.existingFilename)}`
                                                    : <span style={{ color: (docFormErrors.original_filename || docFileError) ? '#f87171' : '#64748b' }}>No file selected (Required)</span>)}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                                        * Allowed file types: PDF, DOC, DOCX, XLS, XLSX &bull; Maximum file size: 5 MB &bull; Maximum 2,000 characters
                                    </div>
                                    {(docFormErrors.original_filename || docFileError) && (
                                        <p className="site-form-error" style={{ marginTop: '4px' }}>
                                            {docFormErrors.original_filename || docFileError}
                                        </p>
                                    )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Effective Date *</label>
                                        <SupplierDatePicker
                                            value={docFormData.effective_date}
                                            error={Boolean(docFormErrors.effective_date)}
                                            onChange={e => handleEffectiveDateChange(e.target.value)}
                                            max={docFormData.expiry_date || undefined}
                                            placeholder="DD-MMM-YYYY"
                                        />
                                        {docFormErrors.effective_date && (
                                            <p className="site-form-error">{docFormErrors.effective_date}</p>
                                        )}
                                    </div>

                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Expiry Date *</label>
                                        <SupplierDatePicker
                                            value={docFormData.expiry_date}
                                            error={Boolean(docFormErrors.expiry_date)}
                                            onChange={e => handleExpiryDateChange(e.target.value)}
                                            min={docFormData.effective_date || undefined}
                                            placeholder="DD-MMM-YYYY"
                                        />
                                        {docFormErrors.expiry_date && (
                                            <p className="site-form-error">{docFormErrors.expiry_date}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="comp-form-actions" style={{ marginTop: '20px' }}>
                                    <button
                                        type="button"
                                        className="comp-btn-cancel"
                                        onClick={handleCloseDocModal}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="comp-btn-save" disabled={isDocSubmitting}>
                                        {isDocSubmitting ? (
                                            <>
                                                <Loader2 size={14} className="spin-icon" style={{ display: 'inline', marginRight: 6 }} />
                                                {editingDocId ? 'Updating...' : 'Uploading...'}
                                            </>
                                        ) : (
                                            editingDocId ? 'Update Document' : 'Save & Upload Document'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ── VIEW / PREVIEW DOCUMENT MODAL ───────────────────────────────── */}
            <AnimatePresence>
                {previewingDoc && (
                    <div className="sup-modal-overlay" onClick={() => setPreviewingDoc(null)}>
                        <motion.div
                            className="sup-doc-view-box"
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.94 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="sup-modal-header">
                                <div>
                                    <h3 className="sup-modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FileCheck size={18} color="#00bcff" />
                                        Document Preview & Details
                                    </h3>
                                    <p className="sup-modal-subtitle">
                                        Supplier: {previewingDoc.supplier.code} - {previewingDoc.supplier.name}
                                    </p>
                                </div>
                                <button
                                    className="sup-modal-close"
                                    onClick={() => setPreviewingDoc(null)}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="sup-modal-body">
                                <div className="sup-doc-preview-card">
                                    <div className="sup-doc-icon-large">
                                        <FileText size={28} />
                                    </div>
                                    <div className="sup-doc-info-meta">
                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#f8fafc', fontWeight: 600 }}>
                                            {previewingDoc.doc.document_name || previewingDoc.doc.name}
                                        </h4>
                                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                                            Type: <strong style={{ color: '#cbd5e1' }}>{getDocumentTypeName(previewingDoc.doc)}</strong>
                                        </span>
                                        <span
                                            style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <span>Filename: <strong style={{ color: '#00bcff' }}>{getDisplayFilename(previewingDoc.doc.original_filename || previewingDoc.doc.originalFilename)}</strong></span>
                                        </span>
                                    </div>
                                    <div>
                                        <span className={`sup-doc-badge ${previewingDoc.doc.status ? 'active' : 'inactive'}`}>
                                            {previewingDoc.doc.status ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>

                                <div className="sup-details-grid" style={{ padding: '14px', margin: '0 0 10px 0' }}>
                                    <div className="sup-detail-field">
                                        <span className="sup-detail-label">Effective Date</span>
                                        <span className="sup-detail-value">{formatDisplayDate(previewingDoc.doc.effective_date || previewingDoc.doc.effectiveDate)}</span>
                                    </div>
                                    <div className="sup-detail-field">
                                        <span className="sup-detail-label">Expiry Date</span>
                                        <span className="sup-detail-value">{formatDisplayDate(previewingDoc.doc.expiry_date || previewingDoc.doc.expiryDate)}</span>
                                    </div>
                                    <div className="sup-detail-field">
                                        <span className="sup-detail-label">Uploaded UTC Timestamp</span>
                                        <span className="sup-detail-value highlight">{formatUtcDisplay(previewingDoc.doc.uploaded_utc || previewingDoc.doc.uploadedUtc)}</span>
                                    </div>
                                    <div className="sup-detail-field">
                                        <span className="sup-detail-label">Created By</span>
                                        <span className="sup-detail-value">{previewingDoc.doc.created_by || '—'}</span>
                                    </div>
                                    {previewingDoc.doc.storage_key && (
                                        <div className="sup-detail-field" style={{ gridColumn: 'span 2' }}>
                                            <span className="sup-detail-label">Storage Key</span>
                                            <span className="sup-detail-value" style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                                                {previewingDoc.doc.storage_key}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="sup-modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <button
                                    className="sup-topbar-action-btn"
                                    style={{
                                        padding: '8px 16px',
                                        background: 'rgba(34,197,94,0.1)',
                                        color: '#4ade80',
                                        borderColor: 'rgba(34,197,94,0.3)',
                                        opacity: downloadingDocId === (previewingDoc.doc.supplier_document_id ?? previewingDoc.doc.id) ? 0.6 : 1,
                                        cursor: downloadingDocId === (previewingDoc.doc.supplier_document_id ?? previewingDoc.doc.id) ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => handleDownloadDocument(previewingDoc.doc)}
                                    disabled={downloadingDocId === (previewingDoc.doc.supplier_document_id ?? previewingDoc.doc.id)}
                                    title="Download file to computer"
                                >
                                    {downloadingDocId === (previewingDoc.doc.supplier_document_id ?? previewingDoc.doc.id) ? (
                                        <RefreshCw size={14} className="spin-icon" />
                                    ) : (
                                        <Download size={14} />
                                    )}
                                    <span>{downloadingDocId === (previewingDoc.doc.supplier_document_id ?? previewingDoc.doc.id) ? 'Downloading...' : 'Download File'}</span>
                                </button>
                                <button
                                    className="comp-btn-save"
                                    style={{ padding: '8px 22px' }}
                                    onClick={() => setPreviewingDoc(null)}
                                >
                                    Close Preview
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ── TAB 4 ADD / EDIT COMPONENT MODAL (API: /api/supplier-components/create/ or /update/) ── */}
            <AnimatePresence>
                {compModalSupplierId && (
                    <div className="sup-modal-overlay" onClick={() => !isSubmitting && setCompModalSupplierId(null)}>
                        <motion.div
                            className="sup-comp-modal-box"
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.94 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="sup-modal-header" style={{ padding: '0 0 16px 0' }}>
                                <div>
                                    <h3 className="sup-modal-title">
                                        {editingCompId ? 'Edit Sourced Component' : 'Map Component to Supplier'}
                                    </h3>
                                    <p className="sup-modal-subtitle">
                                        Configure supplier part number, unit pricing, tax, and delivery terms.
                                    </p>
                                </div>
                                <button
                                    className="sup-modal-close"
                                    onClick={() => !isSubmitting && setCompModalSupplierId(null)}
                                    disabled={isSubmitting}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleSaveComponentMapping} style={{ marginTop: '16px' }}>
                                {compFormErrors.submit && (
                                    <div
                                        style={{
                                            background: 'rgba(239, 68, 68, 0.12)',
                                            border: '1px solid rgba(239, 68, 68, 0.35)',
                                            color: '#f87171',
                                            padding: '10px 14px',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            marginBottom: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        <AlertCircle size={16} style={{ flexShrink: 0 }} />
                                        <span>{compFormErrors.submit}</span>
                                    </div>
                                )}

                                {/* Select from Design Components Catalog */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Select Design Component *</label>
                                    <div className="comp-form-select-wrap">
                                        <select
                                            className={`comp-form-select${compFormErrors.component_id ? ' error' : ''}`}
                                            value={compFormData.component_id}
                                            onChange={e => handleSelectCatalogComponent(e.target.value)}
                                            required
                                        >
                                            <option value="">-- Choose Component from Catalog --</option>
                                            {catalogComponents.map(c => {
                                                const isAlreadyAdded = (componentsMap[compModalSupplierId] || []).some(
                                                    sc => (!editingCompId || String(sc.id) !== String(editingCompId)) &&
                                                        ((sc.component_id && String(sc.component_id) === String(c.id)) ||
                                                            (sc.code && c.code && sc.code.toLowerCase() === c.code.toLowerCase()))
                                                );
                                                return (
                                                    <option
                                                        key={c.id}
                                                        value={c.id}
                                                        disabled={isAlreadyAdded}
                                                        style={isAlreadyAdded ? { color: '#64748b', fontStyle: 'italic', background: '#0f172a' } : {}}
                                                    >
                                                        {c.code} - {c.name} {c.category ? `(${c.category})` : ''} {isAlreadyAdded ? ' ✓ [Already Added]' : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <ChevronDown size={14} className="comp-form-select-icon" />
                                    </div>
                                    {compFormErrors.component_id && (
                                        <p
                                            className="site-form-error"
                                            style={{
                                                color: '#f87171',
                                                fontSize: '12px',
                                                marginTop: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                background: 'rgba(239, 68, 68, 0.1)',
                                                padding: '6px 10px',
                                                borderRadius: '4px',
                                                border: '1px solid rgba(239, 68, 68, 0.25)',
                                            }}
                                        >
                                            <AlertCircle size={14} style={{ flexShrink: 0 }} />
                                            <span>{compFormErrors.component_id}</span>
                                        </p>
                                    )}
                                </div>

                                {/* HSN Code */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">HSN Code</label>
                                    <input
                                        className="comp-form-input"
                                        type="text"
                                        name="hsn_code"
                                        value={compFormData.hsn_code || ''}
                                        onChange={e => setCompFormData(prev => ({ ...prev, hsn_code: e.target.value }))}
                                        placeholder="Auto-populated from selected component"
                                    />
                                    {compFormErrors.hsn_code && <p className="site-form-error">{compFormErrors.hsn_code}</p>}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Supplier Part Number</label>
                                        <input
                                            className={`comp-form-input${compFormErrors.supplier_part_number ? ' error' : ''}`}
                                            type="text"
                                            value={compFormData.supplier_part_number}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setCompFormData(prev => ({ ...prev, supplier_part_number: val }));
                                                if (compFormErrors.supplier_part_number || compFormErrors.submit) {
                                                    setCompFormErrors(prev => {
                                                        const next = { ...prev };
                                                        delete next.supplier_part_number;
                                                        delete next.submit;
                                                        return next;
                                                    });
                                                }
                                            }}
                                            placeholder="e.g. ABC-10012"
                                        />
                                        {compFormErrors.supplier_part_number && (
                                            <p className="site-form-error">{compFormErrors.supplier_part_number}</p>
                                        )}
                                    </div>

                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Operating Currency</label>
                                        <div className="comp-form-select-wrap">
                                            <select
                                                className="comp-form-select"
                                                value={compFormData.currency_code}
                                                onChange={e => setCompFormData(prev => ({ ...prev, currency_code: e.target.value }))}
                                            >
                                                <option value="INR">INR (₹)</option>
                                                <option value="USD">USD ($)</option>
                                                <option value="EUR">EUR (€)</option>
                                                <option value="GBP">GBP (£)</option>
                                            </select>
                                            <ChevronDown size={14} className="comp-form-select-icon" />
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Price (Unit Cost) *</label>
                                        <input
                                            className={`comp-form-input${compFormErrors.price ? ' error' : ''}`}
                                            type="number"
                                            step="0.0001"
                                            value={compFormData.price}
                                            onChange={e => setCompFormData(prev => ({ ...prev, price: e.target.value }))}
                                            placeholder="e.g. 100.00"
                                            required
                                        />
                                        {compFormErrors.price && <p className="site-form-error">{compFormErrors.price}</p>}
                                    </div>

                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Tax Rate (%)</label>
                                        <input
                                            className="comp-form-input"
                                            type="number"
                                            step="0.01"
                                            value={compFormData.tax}
                                            onChange={e => setCompFormData(prev => ({ ...prev, tax: e.target.value }))}
                                            placeholder="e.g. 18.00"
                                        />
                                    </div>
                                </div>

                                {/* Live Calculated Total Preview */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(0,188,255,0.06)', borderRadius: '6px', border: '1px solid rgba(0,188,255,0.2)' }}>
                                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>Calculated Total (Price + Tax):</span>
                                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#00bcff' }}>
                                        {compFormData.currency_code} {calcTotal}
                                    </span>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Min Order Qty</label>
                                        <input
                                            className="comp-form-input"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={compFormData.minimum_order_quantity}
                                            onChange={e => setCompFormData(prev => ({ ...prev, minimum_order_quantity: e.target.value }))}
                                            placeholder="10.00"
                                        />
                                    </div>

                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Lead Time (Days)</label>
                                        <input
                                            className="comp-form-input"
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={compFormData.lead_time_days}
                                            onChange={e => setCompFormData(prev => ({ ...prev, lead_time_days: e.target.value }))}
                                            placeholder="15"
                                        />
                                    </div>
                                </div>

                                {/* Warranty Terms */}
                                <div className="comp-form-group" style={{ marginTop: '12px' }}>
                                    <label className="comp-form-label">Warranty Terms *</label>
                                    <div className="comp-form-select-wrap">
                                        <select
                                            className={`comp-form-select${compFormErrors.warranty_terms ? ' error' : ''}`}
                                            name="warranty_terms"
                                            value={compFormData.warranty_terms}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setCompFormData(prev => ({ ...prev, warranty_terms: val }));
                                                if (compFormErrors.warranty_terms) {
                                                    setCompFormErrors(prev => {
                                                        const next = { ...prev };
                                                        delete next.warranty_terms;
                                                        return next;
                                                    });
                                                }
                                            }}
                                            disabled={loadingWarrantyTerms}
                                        >
                                            <option value="">{loadingWarrantyTerms ? 'Loading warranty terms...' : 'Select Warranty Terms'}</option>
                                            {warrantyTermsList.map(w => (
                                                <option key={w.warranty_terms_id} value={w.warranty_terms_id}>
                                                    {w.warranty_terms}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={14} className="comp-form-select-icon" />
                                    </div>
                                    {compFormErrors.warranty_terms && <p className="site-form-error">{compFormErrors.warranty_terms}</p>}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                                    <div className="comp-form-group">
                                        <label className="comp-form-label">Price Valid Until</label>
                                        <SupplierDatePicker
                                            value={compFormData.price_valid_until}
                                            onChange={e => setCompFormData(prev => ({ ...prev, price_valid_until: e.target.value }))}
                                            placeholder="DD-MMM-YYYY"
                                        />
                                    </div>

                                    <div className="comp-form-group" style={{ justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#cbd5e1', cursor: 'pointer', marginTop: '14px' }}>
                                            <input
                                                type="checkbox"
                                                checked={compFormData.is_preferred}
                                                onChange={e => setCompFormData(prev => ({ ...prev, is_preferred: e.target.checked }))}
                                            />
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <Star size={13} color="#fbbf24" fill={compFormData.is_preferred ? '#fbbf24' : 'none'} />
                                                Preferred Sourcing Component
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                <div className="comp-form-actions" style={{ marginTop: '20px' }}>
                                    <button
                                        type="button"
                                        className="comp-btn-cancel"
                                        onClick={() => setCompModalSupplierId(null)}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="comp-btn-save" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                <Loader2 size={14} className="spin-icon" />
                                                Saving...
                                            </span>
                                        ) : (
                                            editingCompId ? 'Update Mapping' : 'Add to Sourcing List'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ── CONFIRM DELETE MODAL ────────────────────────────────────────── */}
            <AnimatePresence>
                {pendingDelete && (
                    <ConfirmDeleteModal
                        message={`Are you sure you want to delete supplier "${pendingDelete.name}" (${pendingDelete.code})? All associated contacts and component mappings will be removed.`}
                        onConfirm={handleConfirmDelete}
                        onCancel={() => setPendingDelete(null)}
                    />
                )}
            </AnimatePresence>

            {/* ══ 4. FOOTER ═══════════════════════════════════════════════════════ */}
            <InnerFooter />
        </motion.div>
    );
};

export default SupplierList;
